import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { lazy, Suspense, type ReactNode } from 'react';
import { Spin } from 'antd';
import { useAuthBootstrap } from '@/hooks/useAuthBootstrap';
import { useGoldPriceSubscription } from '@/hooks/useGoldPrice';

// Eager pages (entry + list of main modules)
import { LandingPage } from '@/pages/Landing';
import { LoginPage } from '@/pages/Login';
import { POSPage } from '@/pages/POS';
import { ProductsPage } from '@/pages/Products';
import { OrdersPage } from '@/pages/Orders';
import { NotFoundPage } from '@/pages/NotFound';

import { Layout } from '@/components/Layout';
import { PrivateRoute } from '@/components/PrivateRoute';
import { RoleGuard } from '@/components/PrivateRoute/RoleGuard';
import { StoreGuard } from '@/components/PrivateRoute/StoreGuard';
import { FeatureGate } from '@/components/FeatureGate';
import { ROUTES } from '@/constants/routes';

// Lazy pages (less-frequented or heavy)
const InventoryPage = lazy(() => import('@/pages/Inventory').then((m) => ({ default: m.InventoryPage })));
const WarehousesPage = lazy(() => import('@/pages/Inventory/warehouses').then((m) => ({ default: m.WarehousesPage })));
const StaffsPage = lazy(() => import('@/pages/Staffs').then((m) => ({ default: m.StaffsPage })));
const ReportsPage = lazy(() => import('@/pages/Reports').then((m) => ({ default: m.ReportsPage })));
const HardwarePage = lazy(() => import('@/pages/Hardware').then((m) => ({ default: m.HardwarePage })));
const StoreSettingsPage = lazy(() => import('@/pages/StoreSettings').then((m) => ({ default: m.StoreSettingsPage })));
const ThemeSettingsPage = lazy(() => import('@/pages/ThemeSettings').then((m) => ({ default: m.ThemeSettingsPage })));

// Lazy detail / form pages
const ProductDetailPage = lazy(() => import('@/pages/Products/detail').then((m) => ({ default: m.ProductDetailPage })));
const ProductNewPage = lazy(() => import('@/pages/Products/new').then((m) => ({ default: m.ProductNewPage })));
const ProductEditPage = lazy(() => import('@/pages/Products/edit').then((m) => ({ default: m.ProductEditPage })));
const OrderDetailPage = lazy(() => import('@/pages/Orders/detail').then((m) => ({ default: m.OrderDetailPage })));
const OrderEditPage = lazy(() => import('@/pages/Orders/edit').then((m) => ({ default: m.OrderEditPage })));
const StaffDetailPage = lazy(() => import('@/pages/Staffs/detail').then((m) => ({ default: m.StaffDetailPage })));

const SalesReportPage = lazy(() => import('@/pages/Reports/sales').then((m) => ({ default: m.SalesReportPage })));
const InventoryReportPage = lazy(() => import('@/pages/Reports/inventory').then((m) => ({ default: m.InventoryReportPage })));
const EodReportPage = lazy(() => import('@/pages/Reports/eod').then((m) => ({ default: m.EodReportPage })));

// Roles (NEW — /settings/roles) — TODO: EXTENSION BEYOND SITEMAP v1.1
const RolesPage = lazy(() => import('@/pages/Roles').then((m) => ({ default: m.RolesPage })));
const RoleDetailPage = lazy(() => import('@/pages/Roles/detail').then((m) => ({ default: m.RoleDetailPage })));

// Dashboard + Core gaps (Phase 1)
const DashboardPage = lazy(() => import('@/pages/Dashboard').then((m) => ({ default: m.DashboardPage })));
const NotificationsPage = lazy(() => import('@/pages/Notifications').then((m) => ({ default: m.NotificationsPage })));

// Customers CRM
const CustomersPage = lazy(() => import('@/pages/Customers').then((m) => ({ default: m.CustomersPage })));
const CustomerDetailPage = lazy(() => import('@/pages/Customers/detail').then((m) => ({ default: m.CustomerDetailPage })));
const CustomerPurchasesPage = lazy(() => import('@/pages/Customers/purchases').then((m) => ({ default: m.CustomerPurchasesPage })));
const CustomerLoyaltyPage = lazy(() => import('@/pages/Customers/loyalty').then((m) => ({ default: m.CustomerLoyaltyPage })));
const CustomerLoyaltySettingsPage = lazy(() => import('@/pages/Customers/loyalty-settings').then((m) => ({ default: m.CustomerLoyaltySettingsPage })));

// Auth flow
const OtpPage = lazy(() => import('@/pages/Auth/Otp').then((m) => ({ default: m.OtpPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/Auth/ForgotPassword').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/Auth/ResetPassword').then((m) => ({ default: m.ResetPasswordPage })));
const RegisterPage = lazy(() => import('@/pages/Auth/Register').then((m) => ({ default: m.RegisterPage })));
const SelectStorePage = lazy(() => import('@/pages/Auth/SelectStore').then((m) => ({ default: m.SelectStorePage })));
const ChangePasswordPage = lazy(() => import('@/pages/Auth/ChangePassword').then((m) => ({ default: m.ChangePasswordPage })));
// EG-356 Sprint 6 (04b9120) — password-less signup → đặt password lần đầu sau OTP
const SetPasswordPage = lazy(() => import('@/pages/Auth/SetPassword').then((m) => ({ default: m.SetPasswordPage })));

// POS enhancements
const PosCustomerDisplayPage = lazy(() => import('@/pages/POS/CustomerDisplay').then((m) => ({ default: m.CustomerDisplayPage })));
const PosShiftsPage = lazy(() => import('@/pages/POS/Shifts').then((m) => ({ default: m.ShiftsPage })));
const BuybackListPage = lazy(() => import('@/pages/BuyBack').then((m) => ({ default: m.BuybackListPage })));
const BuybackNewPage = lazy(() => import('@/pages/BuyBack/new').then((m) => ({ default: m.BuybackNewPage })));
const BuybackDetailPage = lazy(() => import('@/pages/BuyBack/detail').then((m) => ({ default: m.BuybackDetailPage })));
const BuybackReportsPage = lazy(() => import('@/pages/BuyBack/reports').then((m) => ({ default: m.BuybackReportsPage })));
const ExchangeListPage = lazy(() => import('@/pages/Exchange').then((m) => ({ default: m.ExchangeListPage })));
const ExchangeNewPage = lazy(() => import('@/pages/Exchange/new').then((m) => ({ default: m.ExchangeNewPage })));
const ExchangeDetailPage = lazy(() => import('@/pages/Exchange/detail').then((m) => ({ default: m.ExchangeDetailPage })));
const ExchangeApprovePage = lazy(() => import('@/pages/Exchange/approve').then((m) => ({ default: m.ExchangeApprovePage })));
const ExchangeReportsPage = lazy(() => import('@/pages/Exchange/reports').then((m) => ({ default: m.ExchangeReportsPage })));

// Settings expansion
const ProfileSettingsPage = lazy(() => import('@/pages/Settings/Profile').then((m) => ({ default: m.ProfileSettingsPage })));
const SecuritySettingsPage = lazy(() => import('@/pages/Settings/Security').then((m) => ({ default: m.SecuritySettingsPage })));
const NotificationsSettingsPage = lazy(() => import('@/pages/Settings/Notifications').then((m) => ({ default: m.NotificationsSettingsPage })));
const GeneralSettingsPage = lazy(() => import('@/pages/Settings/General').then((m) => ({ default: m.GeneralSettingsPage })));
const LocaleSettingsPage = lazy(() => import('@/pages/Settings/Locale').then((m) => ({ default: m.LocaleSettingsPage })));
const DataExportPage = lazy(() => import('@/pages/Settings/DataExport').then((m) => ({ default: m.DataExportPage })));

// Phase 2 — Business features (Starter+)
const GoldPriceBoardPage = lazy(() => import('@/pages/GoldPrice').then((m) => ({ default: m.GoldPriceBoardPage })));
const GoldPriceHistoryPage = lazy(() => import('@/pages/GoldPrice/history').then((m) => ({ default: m.GoldPriceHistoryPage })));
const CurrentQuotesPage = lazy(() => import('@/pages/CurrentQuotes').then((m) => ({ default: m.CurrentQuotesPage })));
const InstrumentsPage = lazy(() =>
    import('@/pages/Instruments').then((m) => ({ default: m.InstrumentsPage })),
);
const GoldPriceSettingsPage = lazy(() => import('@/pages/GoldPrice/settings').then((m) => ({ default: m.GoldPriceSettingsPage })));
const GoldPriceTvPage = lazy(() => import('@/pages/GoldPrice/tv').then((m) => ({ default: m.GoldPriceTvPage })));
const GoldPriceWidgetPage = lazy(() => import('@/pages/GoldPrice/widget').then((m) => ({ default: m.GoldPriceWidgetPage })));
const PriceProtectionPage = lazy(() =>
    import('@/pages/GoldPrice/PriceProtection').then((m) => ({ default: m.PriceProtectionPage })),
);
const PriceProtectionLogsPage = lazy(() =>
    import('@/pages/GoldPrice/PriceProtection/logs').then((m) => ({ default: m.PriceProtectionLogsPage })),
);

// Sales Invoices — EG-568, EG-569, EG-570, EG-571, EG-572
const SalesInvoicesPage = lazy(() => import('@/pages/SalesInvoices').then((m) => ({ default: m.SalesInvoicesPage })));
const SalesInvoiceCreatePage = lazy(() => import('@/pages/SalesInvoices/create').then((m) => ({ default: m.SalesInvoiceCreatePage })));
const SalesInvoiceDetailPage = lazy(() => import('@/pages/SalesInvoices/detail').then((m) => ({ default: m.SalesInvoiceDetailPage })));
const InvoiceManagementPage = lazy(() => import('@/pages/SalesInvoices/invoice-management').then((m) => ({ default: m.InvoiceManagementPage })));
const SalesReturnDrawerPage = lazy(() => import('@/pages/SalesInvoices/SalesReturnDrawer').then((m) => ({ default: m.SalesReturnDrawerPage })));
const SalesDiscountDrawerPage = lazy(() => import('@/pages/SalesInvoices/SalesDiscountDrawer').then((m) => ({ default: m.SalesDiscountDrawerPage })));
const DerivedSalesDocumentEditPage = lazy(() => import('@/pages/SalesInvoices/derived-document-edit').then((m) => ({ default: m.DerivedSalesDocumentEditPage })));

const SuppliersPage = lazy(() => import('@/pages/Suppliers').then((m) => ({ default: m.SuppliersPage })));
const SupplierDetailPage = lazy(() => import('@/pages/Suppliers/detail').then((m) => ({ default: m.SupplierDetailPage })));
const PurchasingPage = lazy(() => import('@/pages/Purchasing').then((m) => ({ default: m.PurchasingPage })));
const PurchasingDetailPage = lazy(() => import('@/pages/Purchasing/detail').then((m) => ({ default: m.PurchasingDetailPage })));

const PromotionsPage = lazy(() => import('@/pages/Promotions').then((m) => ({ default: m.PromotionsPage })));
const PromotionCreatePage = lazy(() => import('@/pages/Promotions/create').then((m) => ({ default: m.PromotionCreatePage })));
const VouchersPage = lazy(() => import('@/pages/Promotions/vouchers').then((m) => ({ default: m.VouchersPage })));
const VoucherCreatePage = lazy(() => import('@/pages/Promotions/voucher-create').then((m) => ({ default: m.VoucherCreatePage })));
const FlashSalesPage = lazy(() => import('@/pages/Promotions/flash-sales').then((m) => ({ default: m.FlashSalesPage })));
const LoyaltyPage = lazy(() => import('@/pages/Promotions/loyalty').then((m) => ({ default: m.LoyaltyPage })));
const WholesaleGiftsPage = lazy(() => import('@/pages/Promotions/wholesale-gifts').then((m) => ({ default: m.WholesaleGiftsPage })));
const PromotionReportsPage = lazy(() => import('@/pages/Promotions/reports').then((m) => ({ default: m.PromotionReportsPage })));

const WorkshopPage = lazy(() => import('@/pages/Workshop').then((m) => ({ default: m.WorkshopPage })));
const WorkshopDetailPage = lazy(() => import('@/pages/Workshop/detail').then((m) => ({ default: m.WorkshopDetailPage })));
const CraftsmenPage = lazy(() => import('@/pages/Workshop/craftsmen').then((m) => ({ default: m.CraftsmenPage })));
const WorkshopServicesPage = lazy(() => import('@/pages/Workshop/services').then((m) => ({ default: m.WorkshopServicesPage })));

const JewelryScalePage = lazy(() => import('@/pages/JewelryScale').then((m) => ({ default: m.default })));

const GoldTradingBoardPage = lazy(() => import('@/pages/GoldTrading').then((m) => ({ default: m.GoldTradingBoardPage })));
const GoldTradingBuyPage = lazy(() => import('@/pages/GoldTrading/trade').then((m) => ({ default: m.GoldTradingBuyPage })));
const GoldTradingSellPage = lazy(() => import('@/pages/GoldTrading/trade').then((m) => ({ default: m.GoldTradingSellPage })));
const GoldTradingHistoryPage = lazy(() => import('@/pages/GoldTrading/history').then((m) => ({ default: m.GoldTradingHistoryPage })));
const GoldTradingSettingsPage = lazy(() => import('@/pages/GoldTrading/settings').then((m) => ({ default: m.GoldTradingSettingsPage })));

const OnboardingPage = lazy(() => import('@/pages/Onboarding').then((m) => ({ default: m.OnboardingPage })));
const OnboardingCompletePage = lazy(() => import('@/pages/Onboarding').then((m) => ({ default: m.OnboardingCompletePage })));
const OnboardingBusinessPage = lazy(() => import('@/pages/Onboarding/business').then((m) => ({ default: m.OnboardingBusinessPage })));
const OnboardingStorePage = lazy(() => import('@/pages/Onboarding/store').then((m) => ({ default: m.OnboardingStorePage })));
const OnboardingStaffPage = lazy(() => import('@/pages/Onboarding/staff').then((m) => ({ default: m.OnboardingStaffPage })));
const OnboardingProductsPage = lazy(() => import('@/pages/Onboarding/products').then((m) => ({ default: m.OnboardingProductsPage })));
const OnboardingPaymentPage = lazy(() => import('@/pages/Onboarding/payment').then((m) => ({ default: m.OnboardingPaymentPage })));
const OnboardingInvoicePage = lazy(() => import('@/pages/Onboarding/invoice').then((m) => ({ default: m.OnboardingInvoicePage })));

const SetupIndexPage = lazy(() => import('@/pages/Setup').then((m) => ({ default: m.SetupIndexPage })));
const SetupWizardLayout = lazy(() => import('@/pages/Setup').then((m) => ({ default: m.SetupWizardLayout })));
const SetupBrandingStep = lazy(() => import('@/pages/Setup').then((m) => ({ default: m.BrandingStep })));
const SetupCatalogStep = lazy(() => import('@/pages/Setup').then((m) => ({ default: m.CatalogStep })));
const SetupPluginStep = lazy(() => import('@/pages/Setup').then((m) => ({ default: m.PluginStep })));
const SetupHardwareStep = lazy(() => import('@/pages/Setup').then((m) => ({ default: m.HardwareStep })));
const SetupDemoStep = lazy(() => import('@/pages/Setup').then((m) => ({ default: m.DemoStep })));
const SetupCompletePage = lazy(() => import('@/pages/Setup').then((m) => ({ default: m.SetupCompletePage })));

// Phase 2b — Pawn, Installment, Consignment, Labels, Finance
const PawnListPage = lazy(() => import('@/pages/Pawn').then((m) => ({ default: m.PawnListPage })));
const PawnDetailPage = lazy(() => import('@/pages/Pawn/detail').then((m) => ({ default: m.PawnDetailPage })));
const PawnDueTodayPage = lazy(() => import('@/pages/Pawn/due').then((m) => ({ default: m.PawnDueTodayPage })));
const PawnOverduePage = lazy(() => import('@/pages/Pawn/due').then((m) => ({ default: m.PawnOverduePage })));

const InstallmentListPage = lazy(() => import('@/pages/Installment').then((m) => ({ default: m.InstallmentListPage })));
const InstallmentDetailPage = lazy(() => import('@/pages/Installment/detail').then((m) => ({ default: m.InstallmentDetailPage })));

const ConsignmentListPage = lazy(() => import('@/pages/Consignment').then((m) => ({ default: m.ConsignmentListPage })));
const ConsignmentDetailPage = lazy(() => import('@/pages/Consignment/detail').then((m) => ({ default: m.ConsignmentDetailPage })));

const LabelsPage = lazy(() => import('@/pages/Labels').then((m) => ({ default: m.LabelsPage })));
const LabelEditorPage = lazy(() => import('@/pages/Labels/editor').then((m) => ({ default: m.LabelEditorPage })));
const LabelPrintBatchPage = lazy(() => import('@/pages/Labels/print').then((m) => ({ default: m.LabelPrintBatchPage })));

const FinanceCashBookPage = lazy(() => import('@/pages/Finance/cash-book').then((m) => ({ default: m.FinanceCashBookPage })));
const FinanceVouchersPage = lazy(() => import('@/pages/Finance/vouchers').then((m) => ({ default: m.FinanceVouchersPage })));
const FinanceInvoicesPage = lazy(() => import('@/pages/EInvoice').then((m) => ({ default: m.EinvoiceListPage })));
const FinanceMisaPage = lazy(() => import('@/pages/Finance/misa').then((m) => ({ default: m.FinanceMisaPage })));

// Phase 3 — Online Orders, Multi-store, Reports advanced, Import, Audit, Support

const StoresListPage = lazy(() => import('@/pages/Stores').then((m) => ({ default: m.StoresListPage })));
const StoreDetailPage = lazy(() => import('@/pages/Stores/detail').then((m) => ({ default: m.StoreDetailPage })));
const StoresEditPage = lazy(() => import('@/pages/Stores/edit').then((m) => ({ default: m.StoresEditPage })));
const StoreHardwarePage = lazy(() => import('@/pages/Stores/hardware').then((m) => ({ default: m.StoreHardwarePage })));

const ProfitReportPage = lazy(() => import('@/pages/Reports/advanced/profit').then((m) => ({ default: m.ProfitReportPage })));
const StaffReportPage = lazy(() => import('@/pages/Reports/advanced/staff').then((m) => ({ default: m.StaffReportPage })));
const CustomersReportPage = lazy(() => import('@/pages/Reports/advanced/customers').then((m) => ({ default: m.CustomersReportPage })));
const MultiStoreReportPage = lazy(() => import('@/pages/Reports/advanced/multi-store').then((m) => ({ default: m.MultiStoreReportPage })));
const ReportBuilderPage = lazy(() => import('@/pages/Reports/advanced/builder').then((m) => ({ default: m.ReportBuilderPage })));

const ImportHubPage = lazy(() => import('@/pages/Import').then((m) => ({ default: m.ImportHubPage })));
const ImportProductsPage = lazy(() => import('@/pages/Import').then((m) => ({ default: m.ImportProductsPage })));
const ImportCustomersPage = lazy(() => import('@/pages/Import').then((m) => ({ default: m.ImportCustomersPage })));
const ImportSuppliersPage = lazy(() => import('@/pages/Import').then((m) => ({ default: m.ImportSuppliersPage })));

// EG-345 P4 — moved from /pages/Audit/* mocks → /pages/Settings/* (BE wired)
const AuditLogPage = lazy(() => import('@/pages/Settings/AuditLog').then((m) => ({ default: m.AuditLogPage })));
const ApiKeysPage = lazy(() => import('@/pages/Settings/ApiKeys').then((m) => ({ default: m.ApiKeysPage })));

const SupportHomePage = lazy(() => import('@/pages/Support').then((m) => ({ default: m.SupportHomePage })));
const SupportTicketsPage = lazy(() => import('@/pages/Support/tickets').then((m) => ({ default: m.SupportTicketsPage })));
const SupportTicketNewPage = lazy(() => import('@/pages/Support/tickets').then((m) => ({ default: m.SupportTicketNewPage })));
const ChangelogPage = lazy(() => import('@/pages/Support/changelog').then((m) => ({ default: m.ChangelogPage })));

// Phase 4a — P0 Create forms
const PawnNewPage = lazy(() => import('@/pages/Pawn/new').then((m) => ({ default: m.PawnNewPage })));
const InstallmentNewPage = lazy(() => import('@/pages/Installment/new').then((m) => ({ default: m.InstallmentNewPage })));
const InstallmentCollectionsPage = lazy(() => import('@/pages/Installment/due').then((m) => ({ default: m.InstallmentCollectionsPage })));
const InstallmentCollectPage = lazy(() => import('@/pages/Installment/collect').then((m) => ({ default: m.InstallmentCollectPage })));
const InstallmentSchedulePage = lazy(() => import('@/pages/Installment/schedule').then((m) => ({ default: m.InstallmentSchedulePage })));
const ConsignmentNewPage = lazy(() => import('@/pages/Consignment/new').then((m) => ({ default: m.ConsignmentNewPage })));
const SupplierFormPage = lazy(() => import('@/pages/Suppliers/form').then((m) => ({ default: m.SupplierFormPage })));
const PurchasingNewPage = lazy(() => import('@/pages/Purchasing/new').then((m) => ({ default: m.PurchasingNewPage })));
const PurchasingReceivePage = lazy(() => import('@/pages/Purchasing/receive').then((m) => ({ default: m.PurchasingReceivePage })));
const PurchasingMatchPage = lazy(() => import('@/pages/Purchasing/match').then((m) => ({ default: m.PurchasingMatchPage })));
const WorkshopNewPage = lazy(() => import('@/pages/Workshop/new').then((m) => ({ default: m.WorkshopNewPage })));
const WorkshopCommissionsPage = lazy(() => import('@/pages/Workshop/commissions').then((m) => ({ default: m.WorkshopCommissionsPage })));
const CustomerNewPage = lazy(() => import('@/pages/Customers/new').then((m) => ({ default: m.CustomerNewPage })));
const CustomerCreditsPage = lazy(() => import('@/pages/Customers/credits').then((m) => ({ default: m.CustomerCreditsPage })));
const SupplierHistoryPage = lazy(() => import('@/pages/Suppliers/history').then((m) => ({ default: m.SupplierHistoryPage })));
const PawnSettingsPage = lazy(() => import('@/pages/Pawn/settings').then((m) => ({ default: m.PawnSettingsPage })));
const InstallmentSettingsPage = lazy(() => import('@/pages/Installment/settings').then((m) => ({ default: m.InstallmentSettingsPage })));
const EinvoiceSettingsPage = lazy(() => import('@/pages/EInvoice/settings').then((m) => ({ default: m.EinvoiceSettingsPage })));
const EinvoiceListPage = lazy(() => import('@/pages/EInvoice').then((m) => ({ default: m.EinvoiceListPage })));
const EinvoiceDetailPage = lazy(() => import('@/pages/EInvoice/detail').then((m) => ({ default: m.EinvoiceDetailPage })));
const InvoiceLookupPage = lazy(() => import('@/pages/public/InvoiceLookup').then((m) => ({ default: m.InvoiceLookupPage })));
const EinvoiceStatsPage = lazy(() => import('@/pages/EInvoice/stats').then((m) => ({ default: m.EinvoiceStatsPage })));
const EinvoiceTaxReportPage = lazy(() => import('@/pages/EInvoice/tax-report').then((m) => ({ default: m.EinvoiceTaxReportPage })));
const PaymentMethodsPage = lazy(() => import('@/pages/Finance/payment-methods').then((m) => ({ default: m.PaymentMethodsPage })));
const LabelsPrinterSettingsPage = lazy(() => import('@/pages/Labels/printer-settings').then((m) => ({ default: m.LabelsPrinterSettingsPage })));
const ProductUnitsPage = lazy(() => import('@/pages/Products/units').then((m) => ({ default: m.ProductUnitsPage })));
const SettingsIndexPage = lazy(() => import('@/pages/Settings').then((m) => ({ default: m.SettingsIndexPage })));
const BillingSettingsPage = lazy(() => import('@/pages/Settings/Billing').then((m) => ({ default: m.BillingSettingsPage })));
const BillingHistoryPage = lazy(() => import('@/pages/Settings/BillingHistory').then((m) => ({ default: m.BillingHistoryPage })));
const BillingUpgradePage = lazy(() => import('@/pages/Settings/BillingUpgrade').then((m) => ({ default: m.BillingUpgradePage })));
const BillingPaymentPage = lazy(() => import('@/pages/Settings/BillingPayment').then((m) => ({ default: m.BillingPaymentPage })));
const BillingQRCodePage = lazy(() => import('@/pages/Settings/BillingQRCode').then((m) => ({ default: m.BillingQRCodePage })));
const BillingPaymentResultPage = lazy(() => import('@/pages/Settings/BillingPaymentResult').then((m) => ({ default: m.BillingPaymentResultPage })));
// EG-345 — Business (Settings namespace). Payment methods + E-invoice settings
// nằm ở /finance/* (đã có sẵn) — Settings sidebar chỉ link sang.
const BusinessSettingsPage = lazy(() => import('@/pages/Settings/Business').then((m) => ({ default: m.BusinessSettingsPage })));
const ReceiptTemplateListPage = lazy(() => import('@/pages/Settings/ReceiptTemplate').then((m) => ({ default: m.ReceiptTemplateListPage })));
const ReceiptTemplateEditorPage = lazy(() => import('@/pages/Settings/ReceiptTemplate/editor').then((m) => ({ default: m.ReceiptTemplateEditorPage })));
// EG-508 — Payroll settings
const PayrollSettingsPage = lazy(() => import('@/pages/Payroll/settings').then((m) => ({ default: m.PayrollSettingsPage })));
// EG-509 — Payroll components
const PayrollComponentsPage = lazy(() => import('@/pages/Payroll/components').then((m) => ({ default: m.PayrollComponentsPage })));
// EG-517 — Payroll runs
const PayrollRunsPage = lazy(() => import('@/pages/Payroll/runs').then((m) => ({ default: m.PayrollRunListPage })));
// EG-518 — Payroll run detail
const PayrollRunDetailPage = lazy(() => import('@/pages/Payroll/run-detail').then((m) => ({ default: m.PayrollRunDetailPage })));
// EG-525 — Payroll summary dashboard
const PayrollSummaryDashboard = lazy(() => import('@/pages/Payroll/dashboard').then((m) => ({ default: m.PayrollDashboardPage })));
// EG-526 — S5-HKD payroll register report
const S5HKDPage = lazy(() => import('@/pages/Payroll/reports/s5-hkd').then((m) => ({ default: m.S5HKDReportPage })));
// EG-527 — TNCN Quyết toán Mẫu 02 report
const QTTTNcnPage = lazy(() => import('@/pages/Payroll/reports/qtt-tncn').then((m) => ({ default: m.QttTncnPage })));
// EG-348 Phase D — CRM 360 follow-ups board
const FollowUpsBoardPage = lazy(() => import('@/pages/CRM/follow-ups').then((m) => ({ default: m.FollowUpsBoardPage })));
const CrmDashboardPage = lazy(() => import('@/pages/CRM/dashboard').then((m) => ({ default: m.CrmDashboardPage })));
// EG-375 — Referral Program (BE EG-335)
const ReferralOverviewPage = lazy(() => import('@/pages/Referral').then((m) => ({ default: m.ReferralOverviewPage })));
const ReferralListPage = lazy(() => import('@/pages/Referral/list').then((m) => ({ default: m.ReferralListPage })));
const ReferralLeaderboardPage = lazy(() => import('@/pages/Referral/leaderboard').then((m) => ({ default: m.ReferralLeaderboardPage })));
const ReferralConfigPage = lazy(() => import('@/pages/Referral/config').then((m) => ({ default: m.ReferralConfigPage })));
const PawnReportsPage = lazy(() => import('@/pages/Pawn/reports').then((m) => ({ default: m.PawnReportsPage })));
const InstallmentReportsPage = lazy(() => import('@/pages/Installment/reports').then((m) => ({ default: m.InstallmentReportsPage })));
const ConsignmentReportsPage = lazy(() => import('@/pages/Consignment/reports').then((m) => ({ default: m.ConsignmentReportsPage })));
const GoldTradingReportsPage = lazy(() => import('@/pages/GoldTrading/reports').then((m) => ({ default: m.GoldTradingReportsPage })));
const WorkshopReportsPage = lazy(() => import('@/pages/Workshop/reports').then((m) => ({ default: m.WorkshopReportsPage })));
const PurchasingReportsPage = lazy(() => import('@/pages/Purchasing/reports').then((m) => ({ default: m.PurchasingReportsPage })));
const ProductsReportPage = lazy(() => import('@/pages/Reports/advanced/products').then((m) => ({ default: m.ProductsReportPage })));
const FinanceInvoiceIssuePage = lazy(() => import('@/pages/Finance/invoices-issue').then((m) => ({ default: m.FinanceInvoiceIssuePage })));
const FinanceInvoiceDetailPage = lazy(() => import('@/pages/Finance/invoice-detail').then((m) => ({ default: m.FinanceInvoiceDetailPage })));
const FinanceInvoiceAdjustPage = lazy(() => import('@/pages/Finance/invoice-adjust').then((m) => ({ default: m.FinanceInvoiceAdjustPage })));
const LabelPreviewPage = lazy(() => import('@/pages/Labels/preview').then((m) => ({ default: m.LabelPreviewPage })));
// Inventory Phase 4d
const GoodsReceiptListPage = lazy(() => import('@/pages/Inventory/receipts/list').then((m) => ({ default: m.GoodsReceiptListPage })));
const GoodsReceiptNewPage = lazy(() => import('@/pages/Inventory/receipts/new').then((m) => ({ default: m.GoodsReceiptNewPage })));
const GoodsReceiptDetailPage = lazy(() => import('@/pages/Inventory/receipts/detail').then((m) => ({ default: m.GoodsReceiptDetailPage })));
const GoodsReceiptEditPage = lazy(() => import('@/pages/Inventory/receipts/edit').then((m) => ({ default: m.GoodsReceiptEditPage })));
const GoodsIssueListPage = lazy(() => import('@/pages/Inventory/issues/list').then((m) => ({ default: m.GoodsIssueListPage })));
const GoodsIssueNewPage = lazy(() => import('@/pages/Inventory/issues/new').then((m) => ({ default: m.GoodsIssueNewPage })));
const GoodsIssueDetailPage = lazy(() => import('@/pages/Inventory/issues/detail').then((m) => ({ default: m.GoodsIssueDetailPage })));
const GoodsIssueEditPage = lazy(() => import('@/pages/Inventory/issues/edit').then((m) => ({ default: m.GoodsIssueEditPage })));
const StockTransferListPage = lazy(() => import('@/pages/Inventory/transfers/list').then((m) => ({ default: m.StockTransferListPage })));
const StockTransferNewPage = lazy(() => import('@/pages/Inventory/transfers/new').then((m) => ({ default: m.StockTransferNewPage })));
const StockTransferDetailPage = lazy(() => import('@/pages/Inventory/transfers/detail').then((m) => ({ default: m.StockTransferDetailPage })));
const StockTransferEditPage = lazy(() => import('@/pages/Inventory/transfers/edit').then((m) => ({ default: m.StockTransferEditPage })));
const StockCountListPage = lazy(() => import('@/pages/Inventory/counts/list').then((m) => ({ default: m.StockCountListPage })));
const StockCountNewPage = lazy(() => import('@/pages/Inventory/counts/new').then((m) => ({ default: m.StockCountNewPage })));
const StockCountEnterPage = lazy(() => import('@/pages/Inventory/counts/enter').then((m) => ({ default: m.StockCountEnterPage })));
const StockCountVariancePage = lazy(() => import('@/pages/Inventory/counts/variance').then((m) => ({ default: m.StockCountVariancePage })));
const InventoryMovementsPage = lazy(() => import('@/pages/Inventory/movements').then((m) => ({ default: m.InventoryMovementsPage })));
const InventoryAlertsPage = lazy(() => import('@/pages/Inventory/alerts').then((m) => ({ default: m.InventoryAlertsPage })));
const InventoryReportsPage4d = lazy(() => import('@/pages/Inventory/reports').then((m) => ({ default: m.InventoryReportsPage })));
const InventoryPrintLabelsPage = lazy(() => import('@/pages/Inventory/print-labels').then((m) => ({ default: m.InventoryPrintLabelsPage })));
const InventoryCostingPage = lazy(() => import('@/pages/Inventory/costing').then((m) => ({ default: m.InventoryCostingPage })));
const StoreNewPage = lazy(() => import('@/pages/Stores/new').then((m) => ({ default: m.StoreNewPage })));
const StaffInvitePage = lazy(() => import('@/pages/Staffs/invite').then((m) => ({ default: m.StaffInvitePage })));
const StaffEditPage = lazy(() => import('@/pages/Staffs/edit').then((m) => ({ default: m.StaffEditPage })));
const StaffActivityPage = lazy(() => import('@/pages/Staffs/activity').then((m) => ({ default: m.StaffActivityPage })));
const StaffStoresPage = lazy(() => import('@/pages/Staffs/stores').then((m) => ({ default: m.StaffStoresPage })));
const CategoriesPage = lazy(() => import('@/pages/Products/categories').then((m) => ({ default: m.CategoriesPage })));
const MetalCategoriesPage = lazy(() => import('@/pages/Products/metal-categories').then((m) => ({ default: m.MetalCategoriesPage })));
const BrandsPage = lazy(() => import('@/pages/Products/brands').then((m) => ({ default: m.BrandsPage })));
const AttributesPage = lazy(() => import('@/pages/Products/attributes').then((m) => ({ default: m.AttributesPage })));

// Commission — EG-350
const CommissionDashboardPage = lazy(() => import('@/pages/Commission').then((m) => ({ default: m.CommissionDashboardPage })));
const CommissionMyPage = lazy(() => import('@/pages/Commission/my').then((m) => ({ default: m.CommissionMyPage })));
const CommissionKpiPage = lazy(() => import('@/pages/Commission/kpi').then((m) => ({ default: m.CommissionKpiPage })));
const CommissionLeaderboardPage = lazy(() => import('@/pages/Commission/leaderboard').then((m) => ({ default: m.CommissionLeaderboardPage })));
const CommissionPayoutsPage = lazy(() => import('@/pages/Commission/payouts').then((m) => ({ default: m.CommissionPayoutsPage })));
// EG-350a/b/c — Phase 2 admin pages
const CommissionKpiTargetsPage = lazy(() => import('@/pages/Commission/kpi-targets').then((m) => ({ default: m.CommissionKpiTargetsPage })));
const CommissionRolesPage = lazy(() => import('@/pages/Commission/config/roles').then((m) => ({ default: m.CommissionRolesPage })));
const CommissionProductRatesPage = lazy(() => import('@/pages/Commission/config/product-rates').then((m) => ({ default: m.ProductRatesPage })));
// EG-350d — Phase 3 general config wizard
const CommissionGeneralConfigPage = lazy(() => import('@/pages/Commission/config/general').then((m) => ({ default: m.CommissionGeneralConfigPage })));
// EG-350e/g — Phase 4/6 admin pages
const CommissionLiveSessionsPage = lazy(() => import('@/pages/Commission/live-sessions').then((m) => ({ default: m.CommissionLiveSessionsPage })));
const CommissionReportsPage = lazy(() => import('@/pages/Commission/reports').then((m) => ({ default: m.CommissionReportsPage })));
// EG-350f — Phase 5 review drill-down
const CommissionReviewIndexPage = lazy(() => import('@/pages/Commission/review/index').then((m) => ({ default: m.CommissionReviewIndexPage })));
const CommissionReviewDetailPage = lazy(() => import('@/pages/Commission/review/detail').then((m) => ({ default: m.CommissionReviewDetailPage })));
// P3.1 — Bonus thresholds
const CommissionBonusThresholdsPage = lazy(() => import('@/pages/Commission/config/bonus-thresholds').then((m) => ({ default: m.BonusThresholdsPage })));
// P3.2 — Adjustments + Audit log
const CommissionAdjustmentsPage = lazy(() => import('@/pages/Commission/adjustments').then((m) => ({ default: m.CommissionAdjustmentsPage })));

// EG-520 — Payslip history (self-service)
const PayslipHistoryPage = lazy(() => import('@/pages/Payroll/payslips').then((m) => ({ default: m.PayslipsPage })));

// Shipping — EG-351
const ShippingListPage = lazy(() => import('@/pages/Shipping').then((m) => ({ default: m.ShippingListPage })));
const ShippingNewPage = lazy(() => import('@/pages/Shipping/new').then((m) => ({ default: m.ShippingNewPage })));
const ShippingDetailPage = lazy(() => import('@/pages/Shipping/detail').then((m) => ({ default: m.ShippingDetailPage })));
const PickupLocationsPage = lazy(() => import('@/pages/Shipping/pickup-locations').then((m) => ({ default: m.PickupLocationsPage })));
const GhtkLogsPage = lazy(() => import('@/pages/Shipping/ghtk-logs').then((m) => ({ default: m.GhtkLogsPage })));

// Accounting Settings — S4.1 FE Accounting
const AccountingSettingsPage = lazy(() => import('@/pages/AccountingSettings').then((m) => ({ default: m.AccountingSettingsPage })));
const AccountingReportsPage = lazy(() => import('@/pages/AccountingReports').then((m) => ({ default: m.AccountingReportsPage })));
const BusinessResultsReportPage = lazy(() => import('@/pages/AccountingReports/business-results').then((m) => ({ default: m.BusinessResultsReportPage })));
const BalanceSheetReportPage = lazy(() => import('@/pages/AccountingReports/balance-sheet').then((m) => ({ default: m.BalanceSheetReportPage })));
const TrialBalanceReportPage = lazy(() => import('@/pages/AccountingReports/trial-balance').then((m) => ({ default: m.TrialBalanceReportPage })));
const S2bRevenueLedgerPage = lazy(() => import('@/pages/AccountingReports/s2b-revenue-ledger').then((m) => ({ default: m.S2bRevenueLedgerPage })));
const S2cIncomeExpenseDetailPage = lazy(() => import('@/pages/AccountingReports/s2c-income-expense-detail').then((m) => ({ default: m.S2cIncomeExpenseDetailPage })));
const S2dMaterialsDetailPage = lazy(() => import('@/pages/AccountingReports/s2d-materials-detail').then((m) => ({ default: m.S2dMaterialsDetailPage })));
const S2eCashDetailPage = lazy(() => import('@/pages/AccountingReports/s2e-cash-detail').then((m) => ({ default: m.S2eCashDetailPage })));
const InventorySummaryReportPage = lazy(() => import('@/pages/AccountingReports/inventory-summary').then((m) => ({ default: m.InventorySummaryReportPage })));
const CashBookPage = lazy(() => import('@/pages/AccountingReports/cash-book').then((m) => ({ default: m.CashBookPage })));
const BankDepositBookPage = lazy(() => import('@/pages/AccountingReports/bank-deposit-book').then((m) => ({ default: m.BankDepositBookPage })));
const CashReceiptJournalPage = lazy(() => import('@/pages/AccountingReports/cash-receipt-journal').then((m) => ({ default: m.CashReceiptJournalPage })));
const CashPaymentJournalPage = lazy(() => import('@/pages/AccountingReports/cash-payment-journal').then((m) => ({ default: m.CashPaymentJournalPage })));
const CashFlowStatementPage = lazy(() => import('@/pages/AccountingReports/cash-flow-statement').then((m) => ({ default: m.CashFlowStatementPage })));
const DocumentListingPage = lazy(() => import('@/pages/AccountingReports/document-listing').then((m) => ({ default: m.DocumentListingPage })));
const VoucherListingPage = lazy(() => import('@/pages/AccountingReports/voucher-listing').then((m) => ({ default: m.VoucherListingPage })));
const InvoiceListingPage = lazy(() => import('@/pages/AccountingReports/invoice-listing').then((m) => ({ default: m.InvoiceListingPage })));
const WarehouseVoucherListingPage = lazy(() => import('@/pages/AccountingReports/warehouse-voucher-listing').then((m) => ({ default: m.WarehouseVoucherListingPage })));
const ReceivablePayableListingPage = lazy(() => import('@/pages/AccountingReports/receivable-payable-listing').then((m) => ({ default: m.ReceivablePayableListingPage })));
const CustomersARPage = lazy(() => import('@/pages/AccountingReports/customers-ar').then((m) => ({ default: m.CustomersARPage })));

// Accounting Dashboard — EG-605
const DashboardOverviewPage = lazy(() => import('@/pages/Accounting/dashboard-overview'));

// Accounting Transactions — S4.1 FE Accounting (EG-583 to EG-588)
const TransactionsListPage = lazy(() => import('@/pages/Accounting/transactions-list').then((m) => ({ default: m.TransactionsListPage })));
const ReceiptFormPage = lazy(() => import('@/pages/Accounting/receipt-form').then((m) => ({ default: m.ReceiptFormPage })));
const PaymentFormPage = lazy(() => import('@/pages/Accounting/payment-form').then((m) => ({ default: m.PaymentFormPage })));
const InternalTransferFormPage = lazy(() => import('@/pages/Accounting/internal-transfer-form'));
const PrepaidExpensesPage = lazy(() => import('@/pages/Accounting/prepaid-expenses'));
const PrepaidExpenseFormPage = lazy(() => import('@/pages/Accounting/prepaid-expense-form'));
const FixedAssetGroupsPage = lazy(() => import('@/pages/Accounting/fixed-asset-groups'));
const FixedAssetsPage = lazy(() => import('@/pages/Accounting/fixed-assets'));
const FixedAssetFormPage = lazy(() => import('@/pages/Accounting/fixed-asset-form'));
const FixedAssetDetailPage = lazy(() => import('@/pages/Accounting/fixed-asset-detail'));
const ToolSuppliesPage = lazy(() => import('@/pages/Accounting/tool-supplies'));
const ToolSupplyFormPage = lazy(() => import('@/pages/Accounting/tool-supply-form'));
const ToolSupplyReportsPage = lazy(() => import('@/pages/Accounting/tool-supply-reports'));
const ToolSupplyDetailPage = lazy(() => import('@/pages/Accounting/tool-supply-detail'));
const OtherDocumentPage = lazy(() => import('@/pages/Accounting/other-document'));
const AccountingDataRepairPage = lazy(() => import('@/pages/Accounting/data-repair'));

// Purchasing Accounting (EG-575 to EG-577)
const PurchaseInvoicesPage = lazy(() => import('@/pages/Purchasing/purchase-invoices').then((m) => ({ default: m.PurchaseInvoicesPage })));
const PurchaseInvoiceNewPage = lazy(() => import('@/pages/Purchasing/purchase-invoice-new').then((m) => ({ default: m.PurchaseInvoiceNewPage })));
const PurchaseInvoiceDetailPage = lazy(() => import('@/pages/Purchasing/purchase-invoice-detail').then((m) => ({ default: m.PurchaseInvoiceDetailPage })));
const PurchaseReturnsPage = lazy(() => import('@/pages/Purchasing/purchase-returns').then((m) => ({ default: m.PurchaseReturnsPage })));
const PurchaseReturnNewPage = lazy(() => import('@/pages/Purchasing/purchase-returns-new').then((m) => ({ default: m.PurchaseReturnNewPage })));
const PurchaseReturnDetailPage = lazy(() => import('@/pages/Purchasing/purchase-return-detail').then((m) => ({ default: m.PurchaseReturnDetailPage })));
const PurchaseDiscountsPage = lazy(() => import('@/pages/Purchasing/purchase-discounts').then((m) => ({ default: m.PurchaseDiscountsPage })));
const PurchaseDiscountNewPage = lazy(() => import('@/pages/Purchasing/purchase-discounts-new').then((m) => ({ default: m.PurchaseDiscountNewPage })));
const PurchaseDiscountDetailPage = lazy(() => import('@/pages/Purchasing/purchase-discount-detail').then((m) => ({ default: m.PurchaseDiscountDetailPage })));

// EInvoice Accounting (EG-578)
const GovInvoicesPage = lazy(() => import('@/pages/EInvoice/gov-invoices').then((m) => ({ default: m.GovInvoicesPage })));

// Inventory Accounting (EG-579 to EG-582)
const AccountingReceiptsPage = lazy(() => import('@/pages/Inventory/accounting-receipts').then((m) => ({ default: m.AccountingReceiptsPage })));
const AccountingIssuesPage = lazy(() => import('@/pages/Inventory/accounting-issues').then((m) => ({ default: m.AccountingIssuesPage })));
const AccountingTransfersPage = lazy(() => import('@/pages/Inventory/accounting-transfers').then((m) => ({ default: m.AccountingTransfersPage })));
const ProductsUomPage = lazy(() => import('@/pages/Inventory/products-uom').then((m) => ({ default: m.ProductsUOMPage })));

// Customers AR (EG-573)
const CustomerARManagementPage = lazy(() => import('@/pages/Customers/ar-management'));

function S({ children }: { children: ReactNode }) {
    return (
        <Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>}>
            {children}
        </Suspense>
    );
}

function FinanceEodRedirect() {
    const { date } = useParams<{ date?: string }>();
    const search = date ? `?date=${encodeURIComponent(date)}` : '';
    return <Navigate to={`${ROUTES.reports.eod}${search}`} replace />;
}

function SalesDocumentDetailRedirect() {
    const { id } = useParams<{ id?: string }>();
    return <Navigate to={id ? `${ROUTES.accounting.salesDocuments}/${id}` : ROUTES.accounting.salesDocuments} replace />;
}

function LegacyPathRedirect({ toBase }: { toBase: string }) {
    const params = useParams();
    const location = useLocation();
    const splat = params['*'];
    const suffix = splat ? `/${splat}` : '';
    return <Navigate to={`${toBase}${suffix}${location.search}${location.hash}`} replace />;
}

function LegacyParamRedirect({ toBase, param = 'id' }: { toBase: string; param?: string }) {
    const params = useParams();
    const location = useLocation();
    const value = params[param];
    const suffix = value ? `/${value}` : '';
    return <Navigate to={`${toBase}${suffix}${location.search}${location.hash}`} replace />;
}

/**
 * Singleton provider that opens exactly one WebSocket + polling subscription
 * for gold-price data. Must be rendered ONCE inside the Redux <Provider>.
 * All consumers call useGoldPrice() (read-only selector) — no side effects there.
 */
function GoldPriceProvider({ children }: { children: ReactNode }) {
    useGoldPriceSubscription();
    return <>{children}</>;
}

export function App() {
    const { bootstrapping } = useAuthBootstrap();

    if (bootstrapping) {
        return (
            <div
                style={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f0f2f5',
                }}
            >
                <Spin size="large" tip="Đang khởi tạo..." />
            </div>
        );
    }

    return (
        <GoldPriceProvider>
            <Routes>
                {/* Public — Auth flow (SITEMAP §1) */}
                <Route path="/auth/login" element={<LoginPage />} />
                <Route path="/auth/otp" element={<S><OtpPage /></S>} />
                <Route path="/auth/forgot-password" element={<S><ForgotPasswordPage /></S>} />
                <Route path="/auth/reset-password" element={<S><ResetPasswordPage /></S>} />
                <Route path="/auth/register" element={<S><RegisterPage /></S>} />
                <Route path="/auth/change-password" element={<S><ChangePasswordPage /></S>} />
                {/* EG-356 Sprint 6 — set password lần đầu sau register OTP (PrivateRoute vì cần token mustSetPassword=true) */}
                <Route
                    path="/auth/set-password"
                    element={<PrivateRoute element={<S><SetPasswordPage /></S>} />}
                />
                <Route
                    path="/auth/select-store"
                    element={<PrivateRoute element={<S><SelectStorePage /></S>} />}
                />
                {/* Backward-compat alias: /login → /auth/login */}
                <Route path="/login" element={<Navigate to={ROUTES.auth.login} replace />} />

                {/* POS Customer Display (public, separate screen for customer-facing display) */}
                <Route path="/pos/customer-display" element={<S><PosCustomerDisplayPage /></S>} />

                {/* Gold Price TV Board — SITEMAP §115 (public fullscreen) */}
                <Route path="/gold-price/tv" element={<S><GoldPriceTvPage /></S>} />

                {/* Onboarding Wizard — SITEMAP §263-269 (authenticated owner only) */}
                <Route
                    path="/onboarding"
                    element={<PrivateRoute element={<S><OnboardingPage /></S>} />}
                />
                <Route
                    path="/onboarding/business"
                    element={<PrivateRoute element={<S><OnboardingBusinessPage /></S>} />}
                />
                <Route
                    path="/onboarding/store"
                    element={<PrivateRoute element={<S><OnboardingStorePage /></S>} />}
                />
                <Route
                    path="/onboarding/staff"
                    element={<PrivateRoute element={<S><OnboardingStaffPage /></S>} />}
                />
                <Route
                    path="/onboarding/products"
                    element={<PrivateRoute element={<S><OnboardingProductsPage /></S>} />}
                />
                <Route
                    path="/onboarding/payment"
                    element={<PrivateRoute element={<S><OnboardingPaymentPage /></S>} />}
                />
                <Route
                    path="/onboarding/invoice"
                    element={<PrivateRoute element={<S><OnboardingInvoicePage /></S>} />}
                />
                <Route
                    path="/onboarding/complete"
                    element={<PrivateRoute element={<S><OnboardingCompletePage /></S>} />}
                />

                {/* Phase 2 Setup Wizard — EG-182 (authenticated, optional) */}
                <Route
                    path="/setup"
                    element={<PrivateRoute element={<S><SetupWizardLayout /></S>} />}
                >
                    <Route index element={<SetupIndexPage />} />
                    <Route path="branding" element={<SetupBrandingStep />} />
                    <Route path="catalog" element={<SetupCatalogStep />} />
                    <Route path="plugin" element={<SetupPluginStep />} />
                    <Route path="hardware" element={<SetupHardwareStep />} />
                    <Route path="demo" element={<SetupDemoStep />} />
                </Route>
                <Route
                    path="/setup/complete"
                    element={<PrivateRoute element={<S><SetupCompletePage /></S>} />}
                />

                {/* Public Invoice Lookup (no auth required) */}
                <Route path="/lookup/:code" element={<InvoiceLookupPage />} />

                {/* EG-195 — Landing page (public, redirects to /dashboard if authenticated) */}
                <Route path="/" element={<LandingPage />} />

                {/* Protected */}
                <Route path="/" element={<PrivateRoute element={<Layout />} />}>
                    {/* Dashboard — SITEMAP §12-16 */}
                    <Route path="dashboard" element={<PrivateRoute element={<S><DashboardPage /></S>} />} />

                    {/* Notifications — SITEMAP §17 */}
                    <Route
                        path="notifications"
                        element={<PrivateRoute element={<S><NotificationsPage /></S>} />}
                    />

                    {/* POS — SITEMAP §18. StoreGuard: cashier phải có sid (chọn store) trước khi
            vào POS — owner/manager/inventory/viewer không bị guard chặn (per ADR-011). */}
                    <Route
                        path="pos"
                        element={
                            <PrivateRoute
                                element={<StoreGuard element={<POSPage />} />}
                                requiredPermissions={['pos.use']}
                            />
                        }
                    />

                    {/* Orders aggregate — SITEMAP §6 EG-78 (POS+Online+Livestream+Dealer+Phone) */}
                    <Route path="orders">
                        <Route
                            index
                            element={<PrivateRoute element={<OrdersPage />} requiredPermissions={['orders.view']} />}
                        />
                        <Route
                            path=":id"
                            element={
                                <PrivateRoute
                                    element={<S><OrderDetailPage /></S>}
                                    requiredPermissions={['orders.view']}
                                />
                            }
                        />
                        <Route
                            path=":id/edit"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="starter"><PurchaseInvoiceDetailPage /></FeatureGate></S>} />}
                        />
                        <Route
                            path=":id/edit"
                            element={
                                <PrivateRoute
                                    element={<S><OrderEditPage /></S>}
                                    requiredPermissions={['orders.update']}
                                />
                            }
                        />
                    </Route>


                    <Route path="buyback">
                        <Route
                            index
                            element={<PrivateRoute element={<S><FeatureGate feature="buyback" minTier="starter"><BuybackListPage /></FeatureGate></S>} requiredPermissions={['pos.buy_in']} />}
                        />
                        <Route
                            path="new"
                            element={<PrivateRoute element={<S><FeatureGate feature="buyback" minTier="starter"><BuybackNewPage /></FeatureGate></S>} requiredPermissions={['pos.buy_in']} />}
                        />
                        <Route
                            path="reports"
                            element={<Navigate to={ROUTES.reports.buyback} replace />}
                        />
                        <Route
                            path=":id"
                            element={<PrivateRoute element={<S><FeatureGate feature="buyback" minTier="starter"><BuybackDetailPage /></FeatureGate></S>} requiredPermissions={['pos.buy_in']} />}
                        />
                    </Route>
                    <Route path="exchanges">
                        <Route
                            index
                            element={<PrivateRoute element={<S><FeatureGate feature="exchange" minTier="starter"><ExchangeListPage /></FeatureGate></S>} requiredPermissions={['pos.refund']} />}
                        />
                        <Route
                            path="new"
                            element={<PrivateRoute element={<S><FeatureGate feature="exchange" minTier="starter"><ExchangeNewPage /></FeatureGate></S>} requiredPermissions={['pos.refund']} />}
                        />
                        <Route
                            path="reports"
                            element={<Navigate to={ROUTES.reports.exchange} replace />}
                        />
                        <Route
                            path=":id/approve"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="exchange" minTier="starter"><RoleGuard allowedRoles={['owner', 'manager']}><ExchangeApprovePage /></RoleGuard></FeatureGate></S>}
                                    requiredPermissions={['pos.refund']}
                                />
                            }
                        />
                        <Route
                            path=":id"
                            element={<PrivateRoute element={<S><FeatureGate feature="exchange" minTier="starter"><ExchangeDetailPage /></FeatureGate></S>} requiredPermissions={['pos.refund']} />}
                        />
                    </Route>
                    <Route
                        path="pos/shifts"
                        element={
                            <PrivateRoute
                                element={<StoreGuard element={<S><PosShiftsPage /></S>} />}
                                requiredPermissions={['pos.shifts_manage']}
                            />
                        }
                    />

                    {/* Customers — SITEMAP §162-168 */}
                    <Route path="customers">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={<S><CustomersPage /></S>}
                                    requiredPermissions={['customers.view']}
                                />
                            }
                        />
                        <Route
                            path="new"
                            element={
                                <PrivateRoute
                                    element={<S><CustomerNewPage /></S>}
                                    requiredPermissions={['customers.manage']}
                                />
                            }
                        />
                        <Route
                            path="loyalty-settings"
                            element={
                                <PrivateRoute
                                    element={<S><CustomerLoyaltySettingsPage /></S>}
                                    requiredPermissions={['customers.loyalty_config']}
                                />
                            }
                        />
                        <Route
                            path=":id"
                            element={
                                <PrivateRoute
                                    element={<S><CustomerDetailPage /></S>}
                                    requiredPermissions={['customers.view']}
                                />
                            }
                        />
                        <Route
                            path=":id/purchases"
                            element={
                                <PrivateRoute
                                    element={<S><CustomerPurchasesPage /></S>}
                                    requiredPermissions={['customers.view']}
                                />
                            }
                        />
                        <Route
                            path=":id/loyalty"
                            element={
                                <PrivateRoute
                                    element={<S><CustomerLoyaltyPage /></S>}
                                    requiredPermissions={['customers.view']}
                                />
                            }
                        />
                        <Route
                            path=":id/credits"
                            element={
                                <PrivateRoute
                                    element={<S><CustomerCreditsPage /></S>}
                                    requiredPermissions={['customers.view']}
                                />
                            }
                        />
                    </Route>

                    {/* Products — SITEMAP §174-176 */}
                    <Route path="products">
                        <Route
                            index
                            element={<PrivateRoute element={<ProductsPage />} requiredPermissions={['products.view']} />}
                        />
                        <Route
                            path="new"
                            element={
                                <PrivateRoute
                                    element={<S><ProductNewPage /></S>}
                                    requiredPermissions={['products.create']}
                                />
                            }
                        />
                        {/* Categories */}
                        <Route
                            path="categories"
                            element={<PrivateRoute element={<S><CategoriesPage /></S>} requiredPermissions={['categories.view']} />}
                        />

                        {/* Metal Categories */}
                        <Route
                            path="metal-categories"
                            element={<PrivateRoute element={<S><MetalCategoriesPage /></S>} requiredPermissions={['categories.view']} />}
                        />

                        {/* Brands */}
                        <Route
                            path="brands"
                            element={<PrivateRoute element={<S><BrandsPage /></S>} requiredPermissions={['brands.view']} />}
                        />

                        {/* Attributes */}
                        <Route
                            path="attributes"
                            element={<PrivateRoute element={<S><AttributesPage /></S>} requiredPermissions={['attributes.view']} />}
                        />
                        <Route
                            path="units"
                            element={
                                <PrivateRoute
                                    element={<S><ProductUnitsPage /></S>}
                                    requiredPermissions={['products.update']}
                                />
                            }
                        />
                        <Route
                            path=":id"
                            element={
                                <PrivateRoute
                                    element={<S><ProductDetailPage /></S>}
                                    requiredPermissions={['products.view']}
                                />
                            }
                        />
                        <Route
                            path=":id/edit"
                            element={
                                <PrivateRoute
                                    element={<S><ProductEditPage /></S>}
                                    requiredPermissions={['products.update']}
                                />
                            }
                        />
                    </Route>

                    {/* Inventory — SITEMAP §93-110 */}
                    <Route path="inventory">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="inventory" minTier="lite">
                                                <InventoryPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                    requiredPermissions={['inventory.view']}
                                />
                            }
                        />
                        {/* Receipts — §94-96 */}
                        <Route
                            path="receipts"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory" minTier="lite"><GoodsReceiptListPage /></FeatureGate></S>} requiredPermissions={['inventory.stock_in']} />}
                        />
                        <Route
                            path="receipts/new"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory" minTier="lite"><GoodsReceiptNewPage /></FeatureGate></S>} requiredPermissions={['inventory.stock_in']} />}
                        />
                        <Route
                            path="receipts/:id"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory" minTier="lite"><GoodsReceiptDetailPage /></FeatureGate></S>} requiredPermissions={['inventory.view']} />}
                        />
                        <Route
                            path="receipts/:id/edit"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory" minTier="lite"><GoodsReceiptEditPage /></FeatureGate></S>} requiredPermissions={['inventory.stock_in']} />}
                        />
                        {/* Issues — §97-99 */}
                        <Route
                            path="issues"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory" minTier="lite"><GoodsIssueListPage /></FeatureGate></S>} requiredPermissions={['inventory.stock_out']} />}
                        />
                        <Route
                            path="issues/new"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory" minTier="lite"><GoodsIssueNewPage /></FeatureGate></S>} requiredPermissions={['inventory.stock_out']} />}
                        />
                        <Route
                            path="issues/:id"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory" minTier="lite"><GoodsIssueDetailPage /></FeatureGate></S>} requiredPermissions={['inventory.view']} />}
                        />
                        <Route
                            path="issues/:id/edit"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory" minTier="lite"><GoodsIssueEditPage /></FeatureGate></S>} requiredPermissions={['inventory.stock_out']} />}
                        />
                        {/* Transfers — §100-102 [S] */}
                        <Route
                            path="transfers"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory-transfer" minTier="starter"><StockTransferListPage /></FeatureGate></S>} requiredPermissions={['inventory.transfer']} />}
                        />
                        <Route
                            path="transfers/new"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory-transfer" minTier="starter"><StockTransferNewPage /></FeatureGate></S>} requiredPermissions={['inventory.transfer']} />}
                        />
                        <Route
                            path="transfers/:id"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory-transfer" minTier="starter"><StockTransferDetailPage /></FeatureGate></S>} requiredPermissions={['inventory.transfer']} />}
                        />
                        <Route
                            path="transfers/:id/edit"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory-transfer" minTier="starter"><StockTransferEditPage /></FeatureGate></S>} requiredPermissions={['inventory.transfer']} />}
                        />
                        <Route
                            path="transfers/:id/approve"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory-transfer" minTier="starter"><StockTransferDetailPage /></FeatureGate></S>} requiredPermissions={['inventory.transfer']} />}
                        />
                        {/* Counts — §103-106 */}
                        <Route
                            path="counts"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory" minTier="lite"><StockCountListPage /></FeatureGate></S>} requiredPermissions={['inventory.adjust']} />}
                        />
                        <Route
                            path="counts/new"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory" minTier="lite"><StockCountNewPage /></FeatureGate></S>} requiredPermissions={['inventory.adjust']} />}
                        />
                        <Route
                            path="counts/:id/enter"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory" minTier="lite"><StockCountEnterPage /></FeatureGate></S>} requiredPermissions={['inventory.adjust']} />}
                        />
                        <Route
                            path="counts/:id/variance"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory" minTier="lite"><StockCountVariancePage /></FeatureGate></S>} requiredPermissions={['inventory.adjust']} />}
                        />
                        {/* Movements / Alerts / Reports / Print — §107-110 */}
                        <Route
                            path="costing"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory" minTier="lite"><InventoryCostingPage /></FeatureGate></S>} requiredPermissions={['inventory.adjust']} />}
                        />
                        <Route
                            path="movements"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory" minTier="lite"><InventoryMovementsPage /></FeatureGate></S>} requiredPermissions={['inventory.view']} />}
                        />
                        <Route
                            path="alerts"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory" minTier="lite"><InventoryAlertsPage /></FeatureGate></S>} requiredPermissions={['inventory.view']} />}
                        />
                        <Route
                            path="reports"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory" minTier="lite"><InventoryReportsPage4d /></FeatureGate></S>} requiredPermissions={['inventory.view']} />}
                        />
                        <Route
                            path="print-labels"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory" minTier="lite"><InventoryPrintLabelsPage /></FeatureGate></S>} requiredPermissions={['inventory.view']} />}
                        />
                        {/* Warehouses — quản lý kho theo cửa hàng */}
                        <Route
                            path="warehouses"
                            element={<PrivateRoute element={<S><FeatureGate feature="inventory" minTier="lite"><WarehousesPage /></FeatureGate></S>} requiredPermissions={['inventory.view']} />}
                        />
                    </Route>

                    {/* Staff — SITEMAP §125-130 */}
                    <Route path="staff">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="staff" minTier="lite">
                                                <StaffsPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                    requiredPermissions={['staff.view']}
                                />
                            }
                        />
                        <Route
                            path="invite"
                            element={
                                <PrivateRoute
                                    element={<S><StaffInvitePage /></S>}
                                    requiredPermissions={['staff.manage']}
                                />
                            }
                        />
                        <Route
                            path=":id/edit"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="staff" minTier="lite"><StaffEditPage /></FeatureGate></S>}
                                    requiredPermissions={['staff.manage']}
                                />
                            }
                        />
                        <Route
                            path=":id/activity"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="staff" minTier="lite"><StaffActivityPage /></FeatureGate></S>}
                                    requiredPermissions={['staff.view']}
                                />
                            }
                        />
                        <Route
                            path=":id/stores"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="staff" minTier="professional"><StaffStoresPage /></FeatureGate></S>}
                                    requiredPermissions={['staff.manage']}
                                />
                            }
                        />
                        <Route
                            path=":id"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="staff" minTier="lite">
                                                <StaffDetailPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                    requiredPermissions={['staff.manage']}
                                />
                            }
                        />
                        {/* EG-510 — Payroll Profile tab route (renders StaffDetailPage with payroll tab active) */}
                        <Route
                            path=":id/payroll-profile"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="staff" minTier="lite">
                                                <StaffDetailPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                    requiredPermissions={['staff.manage']}
                                />
                            }
                        />
                    </Route>

                    {/* Reports — SITEMAP §151-161 */}
                    <Route path="reports">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="reports-basic" minTier="lite">
                                                <ReportsPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                    requiredPermissions={['reports.revenue', 'reports.inventory']}
                                />
                            }
                        />
                        <Route
                            path="revenue"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="reports-basic" minTier="lite">
                                                <SalesReportPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                    requiredPermissions={['reports.revenue']}
                                />
                            }
                        />
                        <Route
                            path="inventory"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="reports-basic" minTier="lite">
                                                <InventoryReportPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                    requiredPermissions={['reports.inventory']}
                                />
                            }
                        />
                        <Route
                            path="eod"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="reports-basic" minTier="lite">
                                                <RoleGuard allowedRoles={['owner', 'manager']}>
                                                    <EodReportPage />
                                                </RoleGuard>
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="pawn"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="pawn" minTier="starter"><RoleGuard allowedRoles={['owner', 'manager']}><PawnReportsPage /></RoleGuard></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="installment"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="installment" minTier="professional"><RoleGuard allowedRoles={['owner', 'manager']}><InstallmentReportsPage /></RoleGuard></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="workshop"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="workshop" minTier="starter"><RoleGuard allowedRoles={['owner', 'manager']}><WorkshopReportsPage /></RoleGuard></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="exchange"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="exchange" minTier="starter"><RoleGuard allowedRoles={['owner', 'manager']}><ExchangeReportsPage /></RoleGuard></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="buyback"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="buyback" minTier="starter"><RoleGuard allowedRoles={['owner', 'manager']}><BuybackReportsPage /></RoleGuard></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="accounting"
                            element={<Navigate to={ROUTES.accounting.reports} replace />}
                        />
                        <Route
                            path="business-results"
                            element={<Navigate to={ROUTES.accounting.reportBusinessResults} replace />}
                        />
                        <Route
                            path="balance-sheet"
                            element={<Navigate to={ROUTES.accounting.reportBalanceSheet} replace />}
                        />
                        <Route
                            path="trial-balance"
                            element={<Navigate to={ROUTES.accounting.reportTrialBalance} replace />}
                        />
                        <Route
                            path="s2b-revenue"
                            element={<Navigate to={ROUTES.accounting.reportS2b} replace />}
                        />
                        <Route
                            path="s2c-income-expense"
                            element={<Navigate to={ROUTES.accounting.reportS2c} replace />}
                        />
                        <Route
                            path="s2d-materials"
                            element={<Navigate to={ROUTES.accounting.reportS2d} replace />}
                        />
                        <Route
                            path="s2e-cash"
                            element={<Navigate to={ROUTES.accounting.reportS2e} replace />}
                        />
                        <Route
                            path="inventory-summary"
                            element={<Navigate to={ROUTES.accounting.reportInventorySummary} replace />}
                        />
                        <Route
                            path="cash-book"
                            element={<Navigate to={ROUTES.accounting.reportCashBook} replace />}
                        />
                        <Route
                            path="document-listing"
                            element={<Navigate to={ROUTES.accounting.reportDocumentListing} replace />}
                        />
                        <Route
                            path="voucher-listing"
                            element={<Navigate to={ROUTES.accounting.reportVoucherListing} replace />}
                        />
                        <Route
                            path="invoice-listing"
                            element={<Navigate to={ROUTES.accounting.reportInvoiceListing} replace />}
                        />
                        <Route
                            path="warehouse-voucher-listing"
                            element={<Navigate to={ROUTES.accounting.reportWarehouseVoucher} replace />}
                        />
                        <Route
                            path="receivable-payable-listing"
                            element={<Navigate to={ROUTES.accounting.reportReceivablePayable} replace />}
                        />
                        <Route
                            path="customers-ar"
                            element={<Navigate to={ROUTES.accounting.reportCustomersAR} replace />}
                        />
                    </Route>

                    {/* Settings — SITEMAP §185-197 */}
                    <Route path="settings">
                        <Route
                            index
                            element={<PrivateRoute element={<S><SettingsIndexPage /></S>} />}
                        />
                        {/* EG-345 P1 — Business info form (was StoreSettingsPage placeholder) */}
                        <Route
                            path="business"
                            element={
                                <PrivateRoute
                                    element={<S><BusinessSettingsPage /></S>}
                                    requiredPermissions={['settings.business']}
                                />
                            }
                        />
                        {/* StoreSettings: /settings/stores → quản lý chi nhánh (preserve for sitemap) */}
                        <Route
                            path="stores"
                            element={
                                <PrivateRoute
                                    element={<S><StoreSettingsPage /></S>}
                                    requiredPermissions={['settings.business']}
                                />
                            }
                        />
                        <Route
                            path="appearance"
                            element={
                                <PrivateRoute
                                    element={<S><ThemeSettingsPage /></S>}
                                    requiredPermissions={['settings.theme']}
                                />
                            }
                        />
                        <Route
                            path="hardware"
                            element={
                                <PrivateRoute
                                    element={<S><HardwarePage /></S>}
                                    requiredPermissions={['settings.hardware']}
                                />
                            }
                        />
                        <Route
                            path="billing"
                            element={
                                <PrivateRoute
                                    element={<S><BillingSettingsPage /></S>}
                                    requiredPermissions={['settings.billing']}
                                />
                            }
                        />
                        <Route
                            path="billing/history"
                            element={
                                <PrivateRoute
                                    element={<S><BillingHistoryPage /></S>}
                                    requiredPermissions={['settings.billing']}
                                />
                            }
                        />
                        <Route
                            path="billing/upgrade"
                            element={
                                <PrivateRoute
                                    element={<S><BillingUpgradePage /></S>}
                                    requiredPermissions={['settings.billing']}
                                />
                            }
                        />
                        <Route
                            path="billing/payment"
                            element={
                                <PrivateRoute
                                    element={<S><BillingPaymentPage /></S>}
                                    requiredPermissions={['settings.billing']}
                                />
                            }
                        />
                        <Route
                            path="billing/payment/qr"
                            element={
                                <PrivateRoute
                                    element={<S><BillingQRCodePage /></S>}
                                    requiredPermissions={['settings.billing']}
                                />
                            }
                        />
                        <Route
                            path="billing/payment/result"
                            element={
                                <PrivateRoute
                                    element={<S><BillingPaymentResultPage /></S>}
                                    requiredPermissions={['settings.billing']}
                                />
                            }
                        />
                        {/* Accounting Settings — S4.1 FE Accounting */}
                        <Route
                            path="accounting"
                            element={<LegacyPathRedirect toBase={ROUTES.accounting.settings} />}
                        />
                        {/* Roles — NEW, EXTENSION BEYOND SITEMAP v1.1 */}
                        <Route
                            path="roles"
                            element={
                                <PrivateRoute
                                    element={<S><RolesPage /></S>}
                                    requiredPermissions={['settings.roles']}
                                />
                            }
                        />
                        <Route
                            path="roles/:id"
                            element={
                                <PrivateRoute
                                    element={<S><RoleDetailPage /></S>}
                                    requiredPermissions={['settings.roles']}
                                />
                            }
                        />
                        {/* Personal settings — all authenticated users */}
                        <Route
                            path="profile"
                            element={<PrivateRoute element={<S><ProfileSettingsPage /></S>} />}
                        />
                        <Route
                            path="security"
                            element={<PrivateRoute element={<S><SecuritySettingsPage /></S>} />}
                        />
                        {/* Tenant-level settings — owner/manager */}
                        <Route
                            path="notifications"
                            element={
                                <PrivateRoute
                                    element={<S><NotificationsSettingsPage /></S>}
                                    requiredPermissions={['settings.notifications']}
                                />
                            }
                        />
                        <Route
                            path="general"
                            element={
                                <PrivateRoute
                                    element={<S><GeneralSettingsPage /></S>}
                                    requiredPermissions={['settings:manage']}
                                />
                            }
                        />
                        <Route
                            path="locale"
                            element={
                                <PrivateRoute
                                    element={<S><LocaleSettingsPage /></S>}
                                    requiredPermissions={['settings.locale']}
                                />
                            }
                        />
                        <Route
                            path="data-export"
                            element={
                                <PrivateRoute
                                    element={<S><DataExportPage /></S>}
                                    requiredPermissions={['settings.data_export']}
                                />
                            }
                        />
                        {/* EG-508 — Payroll settings */}
                        <Route
                            path="payroll"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="payroll" minTier="starter"><PayrollSettingsPage /></FeatureGate></S>}
                                    requiredPermissions={['settings.payroll']}
                                />
                            }
                        />
                        {/* Receipt Template Designer — Phase 1 */}
                        <Route
                            path="receipt-templates"
                            element={
                                <PrivateRoute
                                    element={<S><ReceiptTemplateListPage /></S>}
                                    requiredPermissions={['settings.business']}
                                />
                            }
                        />
                        <Route
                            path="receipt-templates/new"
                            element={
                                <PrivateRoute
                                    element={<S><ReceiptTemplateEditorPage /></S>}
                                    requiredPermissions={['settings.business']}
                                />
                            }
                        />
                        <Route
                            path="receipt-templates/:id/edit"
                            element={
                                <PrivateRoute
                                    element={<S><ReceiptTemplateEditorPage /></S>}
                                    requiredPermissions={['settings.business']}
                                />
                            }
                        />
                    </Route>

                    {/* Payroll Components — EG-509 */}
                    <Route path="payroll">
                        {/* Payroll Dashboard index — EG-525 */}
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="payroll" minTier="starter"><PayrollSummaryDashboard /></FeatureGate></S>}
                                    requiredPermissions={['payroll.view']}
                                />
                            }
                        />
                        <Route
                            path="components"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="payroll" minTier="starter"><PayrollComponentsPage /></FeatureGate></S>}
                                    requiredPermissions={['payroll.manage']}
                                />
                            }
                        />
                        {/* Payroll Runs — EG-517 */}
                        <Route
                            path="runs"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="payroll" minTier="starter"><PayrollRunsPage /></FeatureGate></S>}
                                    requiredPermissions={['payroll.view']}
                                />
                            }
                        />
                        {/* Payroll Run Detail — EG-518 */}
                        <Route
                            path="runs/:runId"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="payroll" minTier="starter"><PayrollRunDetailPage /></FeatureGate></S>}
                                    requiredPermissions={['payroll.view']}
                                />
                            }
                        />
                        {/* S5-HKD Payroll Register Report — EG-526 */}
                        <Route
                            path="reports/s5-hkd"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="payroll" minTier="starter"><S5HKDPage /></FeatureGate></S>}
                                    requiredPermissions={['payroll.view']}
                                />
                            }
                        />
                        {/* TNCN Quyết toán Mẫu 02 Report — EG-527 */}
                        <Route
                            path="reports/qtt-tncn"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="payroll" minTier="starter"><QTTTNcnPage /></FeatureGate></S>}
                                    requiredPermissions={['payroll.view']}
                                />
                            }
                        />
                    </Route>

                    {/* Self-service Payslip History — EG-520 (all authenticated users) */}
                    <Route
                        path="me/payslips"
                        element={<PrivateRoute element={<S><FeatureGate feature="payroll" minTier="starter"><PayslipHistoryPage /></FeatureGate></S>} />}
                    />

                    {/* Gold Price — SITEMAP §111-117 [S]+ */}
                    <Route path="gold-price">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="gold-price" minTier="starter">
                                                <GoldPriceBoardPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="history"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="gold-price" minTier="starter">
                                                <GoldPriceHistoryPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="current-quotes"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="gold-price" minTier="starter">
                                                <CurrentQuotesPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="instruments"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="gold-price" minTier="starter">
                                                <InstrumentsPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="settings"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="gold-price" minTier="starter">
                                                <GoldPriceSettingsPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="widget"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="gold-price" minTier="starter">
                                                <GoldPriceWidgetPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="price-protection"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="gold-price" minTier="starter">
                                                <RoleGuard allowedRoles={['owner', 'manager']}>
                                                    <PriceProtectionPage />
                                                </RoleGuard>
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="price-protection/logs"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="gold-price" minTier="starter">
                                                <RoleGuard allowedRoles={['owner', 'manager']}>
                                                    <PriceProtectionLogsPage />
                                                </RoleGuard>
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                    </Route>

                    {/* Suppliers + Purchasing — SITEMAP §83-92 [S]+ */}
                    <Route path="suppliers">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="suppliers" minTier="starter">
                                                <SuppliersPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="new"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="suppliers" minTier="starter"><SupplierFormPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path=":id/history"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="suppliers" minTier="starter"><SupplierHistoryPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path=":id"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="suppliers" minTier="starter">
                                                <SupplierDetailPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path=":id/edit"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="suppliers" minTier="starter"><SupplierFormPage /></FeatureGate></S>}
                                />
                            }
                        />
                    </Route>
                    {/* AMIS-style Sales Documents — chứng từ bán hàng */}
                    <Route path="sales/documents/*" element={<LegacyPathRedirect toBase={ROUTES.accounting.salesDocuments} />} />
                    <Route path="sales/e-invoices/*" element={<LegacyPathRedirect toBase={ROUTES.accounting.salesEinvoices} />} />
                    <Route path="accounting/purchase-invoices/*" element={<LegacyPathRedirect toBase={ROUTES.accounting.purchaseInvoices} />} />
                    <Route path="accounting/purchase-returns/*" element={<LegacyPathRedirect toBase={ROUTES.accounting.purchaseReturns} />} />
                    <Route path="accounting/purchase-discounts/*" element={<LegacyPathRedirect toBase={ROUTES.accounting.purchaseDiscounts} />} />
                    <Route path="accounting/gov-invoices/*" element={<LegacyPathRedirect toBase={ROUTES.accounting.govInvoices} />} />

                    <Route path="accounting/sales/documents">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="sales-invoices" minTier="professional">
                                                <SalesInvoicesPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="new"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="sales-invoices" minTier="professional">
                                                <SalesInvoiceCreatePage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="returns/new"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="sales-invoices" minTier="professional">
                                                <SalesReturnDrawerPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="returns/:id/edit"
                            element={<PrivateRoute element={<S><FeatureGate feature="sales-invoices" minTier="professional"><DerivedSalesDocumentEditPage /></FeatureGate></S>} />}
                        />
                        <Route
                            path="discounts/new"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="sales-invoices" minTier="professional">
                                                <SalesDiscountDrawerPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="discounts/:id/edit"
                            element={<PrivateRoute element={<S><FeatureGate feature="sales-invoices" minTier="professional"><DerivedSalesDocumentEditPage /></FeatureGate></S>} />}
                        />
                        <Route
                            path=":id/edit"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="sales-invoices" minTier="professional">
                                                <SalesInvoiceCreatePage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path=":id"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="sales-invoices" minTier="professional">
                                                <SalesInvoiceDetailPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                    </Route>
                    {/* Sales Invoices legacy routes — EG-568, EG-569 */}
                    <Route path="sales/invoices">
                        <Route
                            index
                            element={<Navigate to={ROUTES.accounting.salesDocuments} replace />}
                        />
                        <Route
                            path="new"
                            element={<Navigate to={ROUTES.accounting.salesDocumentNew} replace />}
                        />
                        <Route
                            path="replacement"
                            element={<Navigate to={`${ROUTES.accounting.salesEinvoices}?type=replacement`} replace />}
                        />
                        <Route
                            path="replacement/new"
                            element={<Navigate to={`${ROUTES.accounting.salesEinvoices}?type=replacement`} replace />}
                        />
                        <Route
                            path="returns"
                            element={<Navigate to={`${ROUTES.accounting.salesEinvoices}?type=return`} replace />}
                        />
                        <Route
                            path="returns/new"
                            element={<Navigate to={`${ROUTES.accounting.salesEinvoices}?type=return`} replace />}
                        />
                        <Route
                            path="adjustment"
                            element={<Navigate to={`${ROUTES.accounting.salesEinvoices}?type=adjustment`} replace />}
                        />
                        <Route
                            path="adjustment/new"
                            element={<Navigate to={`${ROUTES.accounting.salesEinvoices}?type=adjustment`} replace />}
                        />
                        <Route
                            path=":id"
                            element={<SalesDocumentDetailRedirect />}
                        />
                    </Route>
                    {/* AMIS-style quản lý hóa đơn chung — gốc + điều chỉnh + thay thế + trả hàng */}
                    <Route path="accounting/invoices/outgoing">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="sales-invoices" minTier="professional">
                                                <InvoiceManagementPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path=":id/edit"
                            element={<PrivateRoute element={<S><FeatureGate feature="sales-invoices" minTier="professional"><DerivedSalesDocumentEditPage /></FeatureGate></S>} />}
                        />
                    </Route>
                    <Route path="accounting/purchasing">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="purchasing" minTier="starter">
                                                <PurchasingPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="new"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="purchasing" minTier="starter"><PurchasingNewPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="reports"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="purchasing" minTier="starter"><PurchasingReportsPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path=":id/receive"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="purchasing" minTier="starter"><PurchasingReceivePage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path=":id/match"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="purchasing" minTier="starter"><PurchasingMatchPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path=":id"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="purchasing" minTier="starter">
                                                <PurchasingDetailPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                    </Route>
                    {/* Purchase Documents — EG-575 */}
                    <Route path="accounting/purchase/documents">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="accounting" minTier="starter">
                                                <PurchaseInvoicesPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="new"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="accounting" minTier="starter"><PurchaseInvoiceNewPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path=":id"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="accounting" minTier="starter"><PurchaseInvoiceDetailPage /></FeatureGate></S>}
                                />
                            }
                        />
                    </Route>
                    {/* Purchase Discounts — EG-576 */}
                    <Route path="accounting/purchase/discounts">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="accounting" minTier="starter">
                                                <PurchaseDiscountsPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="new"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="accounting" minTier="starter"><PurchaseDiscountNewPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path=":id"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="accounting" minTier="starter"><PurchaseDiscountDetailPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path=":id/edit"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="starter"><PurchaseDiscountDetailPage /></FeatureGate></S>} />}
                        />
                    </Route>
                    {/* Purchase Returns — EG-576 */}
                    <Route path="accounting/purchase/returns">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="accounting" minTier="starter">
                                                <PurchaseReturnsPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="new"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="accounting" minTier="starter"><PurchaseReturnNewPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path=":id"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="accounting" minTier="starter"><PurchaseReturnDetailPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path=":id/edit"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="starter"><PurchaseReturnDetailPage /></FeatureGate></S>} />}
                        />
                    </Route>

                    {/* Promotions — SITEMAP §170-173 [S]+, Flash Sale [P] */}
                    <Route path="promotions">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="promotions" minTier="starter">
                                                <PromotionsPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="vouchers"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="promotions" minTier="starter">
                                                <VouchersPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="flash-sales"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="flash-sales" minTier="professional">
                                                <FlashSalesPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="new"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="promotions" minTier="starter">
                                                <PromotionCreatePage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path=":id/edit"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="promotions" minTier="starter">
                                                <PromotionCreatePage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="vouchers/create"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="promotions" minTier="starter">
                                                <VoucherCreatePage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="vouchers/:id/edit"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="promotions" minTier="starter">
                                                <VoucherCreatePage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="loyalty"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="promotions" minTier="starter">
                                                <LoyaltyPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="wholesale-gifts"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="promotions" minTier="professional">
                                                <WholesaleGiftsPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="reports"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="promotions" minTier="starter">
                                                <PromotionReportsPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                    </Route>

                    {/* Workshop — SITEMAP §65-74 [S]+ */}
                    <Route path="workshop">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="workshop" minTier="starter">
                                                <WorkshopPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="craftsmen"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="workshop" minTier="starter">
                                                <CraftsmenPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="services"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="workshop" minTier="starter">
                                                <WorkshopServicesPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="commissions"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="workshop" minTier="starter">
                                                <WorkshopCommissionsPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="new"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="workshop" minTier="starter"><WorkshopNewPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="reports"
                            element={<Navigate to={ROUTES.reports.workshop} replace />}
                        />
                        <Route
                            path=":id"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="workshop" minTier="starter">
                                                <WorkshopDetailPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                    </Route>

                    {/* Jewelry Scale — Module cân trang sức */}
                    <Route path="jewelry-scale">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <JewelryScalePage />
                                        </S>
                                    }
                                />
                            }
                        />
                    </Route>

                    {/* Commission — EG-350 [S]+ — gated tier Starter trở lên */}
                    <Route path="commissions">
                        <Route
                            index
                            element={<PrivateRoute element={<S><FeatureGate feature="commission" minTier="starter"><CommissionDashboardPage /></FeatureGate></S>} />}
                        />
                        <Route
                            path="my"
                            element={<PrivateRoute element={<S><CommissionMyPage /></S>} />}
                        />
                        <Route
                            path="kpi"
                            element={<PrivateRoute element={<S><CommissionKpiPage /></S>} />}
                        />
                        <Route
                            path="leaderboard"
                            element={<PrivateRoute element={<S><CommissionLeaderboardPage /></S>} />}
                        />
                        <Route
                            path="config"
                            element={<Navigate to="/commissions/config/general" replace />}
                        />
                        <Route
                            path="payouts"
                            element={<PrivateRoute element={<S><RoleGuard allowedRoles={['owner']}><CommissionPayoutsPage /></RoleGuard></S>} />}
                        />
                        <Route
                            path="kpi-targets"
                            element={<PrivateRoute element={<S><RoleGuard allowedRoles={['owner', 'manager']}><CommissionKpiTargetsPage /></RoleGuard></S>} />}
                        />
                        <Route
                            path="config/general"
                            element={<PrivateRoute element={<S><RoleGuard allowedRoles={['owner']}><CommissionGeneralConfigPage /></RoleGuard></S>} />}
                        />
                        <Route
                            path="config/roles"
                            element={<PrivateRoute element={<S><RoleGuard allowedRoles={['owner']}><CommissionRolesPage /></RoleGuard></S>} />}
                        />
                        <Route
                            path="config/product-rates"
                            element={<PrivateRoute element={<S><RoleGuard allowedRoles={['owner']}><CommissionProductRatesPage /></RoleGuard></S>} />}
                        />
                        <Route
                            path="config/bonus-thresholds"
                            element={<PrivateRoute element={<S><RoleGuard allowedRoles={['owner']}><CommissionBonusThresholdsPage /></RoleGuard></S>} />}
                        />
                        <Route
                            path="adjustments"
                            element={<PrivateRoute element={<S><RoleGuard allowedRoles={['owner']}><CommissionAdjustmentsPage /></RoleGuard></S>} />}
                        />
                        <Route
                            path="live-sessions"
                            element={<PrivateRoute element={<S><RoleGuard allowedRoles={['owner', 'manager']}><CommissionLiveSessionsPage /></RoleGuard></S>} />}
                        />
                        <Route
                            path="reports"
                            element={<PrivateRoute element={<S><RoleGuard allowedRoles={['owner', 'manager']}><CommissionReportsPage /></RoleGuard></S>} />}
                        />
                        <Route
                            path="review"
                            element={<PrivateRoute element={<S><RoleGuard allowedRoles={['owner']}><CommissionReviewIndexPage /></RoleGuard></S>} />}
                        />
                        <Route
                            path="review/:period/:staffId"
                            element={<PrivateRoute element={<S><RoleGuard allowedRoles={['owner']}><CommissionReviewDetailPage /></RoleGuard></S>} />}
                        />
                    </Route>

                    {/* Gold Trading — SITEMAP §59-64 [S]+ */}
                    <Route path="gold-trading">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="gold-trading" minTier="starter">
                                                <GoldTradingBoardPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="buy"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="gold-trading" minTier="starter">
                                                <GoldTradingBuyPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="sell"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="gold-trading" minTier="starter">
                                                <GoldTradingSellPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="history"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="gold-trading" minTier="starter">
                                                <GoldTradingHistoryPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="settings"
                            element={
                                <PrivateRoute
                                    element={
                                        <S>
                                            <FeatureGate feature="gold-trading" minTier="starter">
                                                <GoldTradingSettingsPage />
                                            </FeatureGate>
                                        </S>
                                    }
                                />
                            }
                        />
                        <Route
                            path="reports"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="gold-trading" minTier="starter"><GoldTradingReportsPage /></FeatureGate></S>}
                                />
                            }
                        />
                    </Route>

                    {/* Pawn — SITEMAP §33-42 [S]+ */}
                    <Route path="pawn">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="pawn" minTier="starter"><PawnListPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="due-today"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="pawn" minTier="starter"><PawnDueTodayPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="overdue"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="pawn" minTier="starter"><PawnOverduePage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="new"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="pawn" minTier="starter"><PawnNewPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="settings"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="pawn" minTier="starter"><PawnSettingsPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="reports"
                            element={<Navigate to={ROUTES.reports.pawn} replace />}
                        />
                        <Route
                            path=":id"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="pawn" minTier="starter"><PawnDetailPage /></FeatureGate></S>}
                                />
                            }
                        />
                    </Route>

                    {/* Installment — SITEMAP §43-51 [P] (tích hợp BIDV) */}
                    <Route path="installment">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="installment" minTier="professional"><InstallmentListPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="new"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="installment" minTier="professional"><InstallmentNewPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="collections"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="installment" minTier="professional"><InstallmentCollectionsPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="settings"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="installment" minTier="professional"><InstallmentSettingsPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="reports"
                            element={<Navigate to={ROUTES.reports.installment} replace />}
                        />
                        <Route
                            path=":id/collect"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="installment" minTier="professional"><InstallmentCollectPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path=":id/schedule"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="installment" minTier="professional"><InstallmentSchedulePage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path=":id"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="installment" minTier="professional"><InstallmentDetailPage /></FeatureGate></S>}
                                />
                            }
                        />
                    </Route>

                    {/* Consignment — SITEMAP §52-58 [S]+ */}
                    <Route path="consignment">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="consignment" minTier="starter"><ConsignmentListPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="new"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="consignment" minTier="starter"><ConsignmentNewPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="reports"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="consignment" minTier="starter"><ConsignmentReportsPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path=":id"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="consignment" minTier="starter"><ConsignmentDetailPage /></FeatureGate></S>}
                                />
                            }
                        />
                    </Route>

                    {/* Labels — SITEMAP §118-124 [S]+ */}
                    <Route path="labels">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="labels" minTier="starter"><LabelsPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="new"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="labels" minTier="starter"><LabelEditorPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path=":id/edit"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="labels" minTier="starter"><LabelEditorPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path=":id/preview"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="labels" minTier="starter"><LabelPreviewPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="printer-settings"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="labels" minTier="starter"><LabelsPrinterSettingsPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="print"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="labels" minTier="starter"><LabelPrintBatchPage /></FeatureGate></S>}
                                />
                            }
                        />
                    </Route>

                    {/* Finance — SITEMAP §138-150 [S]+ HĐĐT, [E] MISA */}
                    <Route path="finance">
                        <Route
                            path="eod"
                            element={<PrivateRoute element={<FinanceEodRedirect />} />}
                        />
                        <Route
                            path="eod/:date"
                            element={<PrivateRoute element={<FinanceEodRedirect />} />}
                        />
                        <Route
                            path="cash-book"
                            element={<PrivateRoute element={<S><FinanceCashBookPage /></S>} />}
                        />
                        <Route
                            path="vouchers"
                            element={<PrivateRoute element={<S><FinanceVouchersPage /></S>} />}
                        />
                        <Route
                            path="invoices"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="e-invoice" minTier="starter"><FinanceInvoicesPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="invoices/issue"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="e-invoice" minTier="starter"><FinanceInvoiceIssuePage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="invoices/:id/adjust"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="e-invoice" minTier="starter"><FinanceInvoiceAdjustPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="invoices/:id"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="e-invoice" minTier="starter"><FinanceInvoiceDetailPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="misa"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="misa" minTier="enterprise"><FinanceMisaPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="einvoice-settings"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="e-invoice" minTier="starter"><EinvoiceSettingsPage /></FeatureGate></S>}
                                    requiredPermissions={['accounting.einvoice']}
                                />
                            }
                        />
                        <Route
                            path="einvoices"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="e-invoice" minTier="starter"><EinvoiceListPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="einvoices/:id"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="e-invoice" minTier="starter"><EinvoiceDetailPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="einvoice/reports/stats"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="e-invoice" minTier="starter"><EinvoiceStatsPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="einvoice/reports/tax"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="e-invoice" minTier="starter"><EinvoiceTaxReportPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="payment-methods"
                            element={
                                <PrivateRoute
                                    element={<S><PaymentMethodsPage /></S>}
                                    requiredPermissions={['settings.business']}
                                />
                            }
                        />
                    </Route>

                    {/* Multi-store — SITEMAP §131-137 [P] */}
                    <Route path="stores">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="multi-store" minTier="professional"><StoresListPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path="new"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="multi-store" minTier="professional"><StoreNewPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path=":id"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="multi-store" minTier="professional"><StoreDetailPage /></FeatureGate></S>}
                                />
                            }
                        />
                        <Route
                            path=":id/edit"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="multi-store" minTier="professional"><StoresEditPage /></FeatureGate></S>}
                                    requiredPermissions={['settings.business']}
                                />
                            }
                        />
                        <Route
                            path=":id/hardware/*"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="multi-store" minTier="professional"><StoreHardwarePage /></FeatureGate></S>}
                                />
                            }
                        />
                    </Route>

                    {/* Reports advanced — SITEMAP §152-161 [P]/[E] */}
                    <Route path="reports/profit" element={<PrivateRoute element={<S><FeatureGate feature="reports-advanced" minTier="professional"><ProfitReportPage /></FeatureGate></S>} />} />
                    <Route path="reports/staff" element={<PrivateRoute element={<S><FeatureGate feature="reports-advanced" minTier="professional"><StaffReportPage /></FeatureGate></S>} />} />
                    <Route path="reports/customers" element={<PrivateRoute element={<S><FeatureGate feature="reports-advanced" minTier="professional"><CustomersReportPage /></FeatureGate></S>} />} />
                    <Route path="reports/products" element={<PrivateRoute element={<S><FeatureGate feature="reports-basic" minTier="lite"><ProductsReportPage /></FeatureGate></S>} />} />
                    <Route path="reports/multi-store" element={<PrivateRoute element={<S><FeatureGate feature="reports-advanced" minTier="professional"><MultiStoreReportPage /></FeatureGate></S>} />} />
                    <Route path="reports/builder" element={<PrivateRoute element={<S><FeatureGate feature="reports-advanced" minTier="enterprise"><ReportBuilderPage /></FeatureGate></S>} />} />

                    {/* Import Wizard — SITEMAP §179-184 [S]+ */}
                    <Route path="import">
                        <Route
                            index
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="csv-import" minTier="starter"><ImportHubPage /></FeatureGate></S>}
                                    requiredPermissions={['products.create', 'customers.manage', 'suppliers.view']}
                                />
                            }
                        />
                        <Route
                            path="products"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="csv-import" minTier="starter"><ImportProductsPage /></FeatureGate></S>}
                                    requiredPermissions={['products.create']}
                                />
                            }
                        />
                        <Route
                            path="customers"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="csv-import" minTier="starter"><ImportCustomersPage /></FeatureGate></S>}
                                    requiredPermissions={['customers.manage']}
                                />
                            }
                        />
                        <Route
                            path="suppliers"
                            element={
                                <PrivateRoute
                                    element={<S><FeatureGate feature="csv-import" minTier="starter"><ImportSuppliersPage /></FeatureGate></S>}
                                    requiredPermissions={['suppliers.view']}
                                />
                            }
                        />
                    </Route>

                    {/* EG-348 Phase D — CRM 360 */}
                    <Route
                        path="crm"
                        element={
                            <PrivateRoute
                                element={
                                    <S>
                                        <FeatureGate feature="crm" minTier="professional">
                                            <CrmDashboardPage />
                                        </FeatureGate>
                                    </S>
                                }
                            />
                        }
                    />
                    <Route
                        path="crm/follow-ups"
                        element={
                            <PrivateRoute
                                element={
                                    <S>
                                        <FeatureGate feature="crm" minTier="professional">
                                            <FollowUpsBoardPage />
                                        </FeatureGate>
                                    </S>
                                }
                            />
                        }
                    />

                    {/* EG-375 / EG-335 — Referral Program (Starter+ tier) */}
                    <Route
                        path="referral"
                        element={
                            <PrivateRoute
                                element={<S><FeatureGate feature="referral" minTier="starter"><ReferralOverviewPage /></FeatureGate></S>}
                            />
                        }
                    />
                    <Route
                        path="referral/list"
                        element={
                            <PrivateRoute
                                element={<S><FeatureGate feature="referral" minTier="starter"><ReferralListPage /></FeatureGate></S>}
                            />
                        }
                    />
                    <Route
                        path="referral/leaderboard"
                        element={
                            <PrivateRoute
                                element={<S><FeatureGate feature="referral" minTier="starter"><ReferralLeaderboardPage /></FeatureGate></S>}
                            />
                        }
                    />
                    <Route
                        path="referral/config"
                        element={
                            <PrivateRoute
                                element={<S><FeatureGate feature="referral" minTier="starter"><ReferralConfigPage /></FeatureGate></S>}
                            />
                        }
                    />

                    {/* Audit log + API Keys — under settings namespace */}
                    <Route
                        path="settings/audit-log"
                        element={
                            <PrivateRoute
                                element={<S><FeatureGate feature="audit-log" minTier="professional"><AuditLogPage /></FeatureGate></S>}
                                requiredPermissions={['settings.audit_log']}
                            />
                        }
                    />
                    <Route
                        path="settings/api-keys"
                        element={
                            <PrivateRoute
                                element={<S><FeatureGate feature="api-keys" minTier="enterprise"><ApiKeysPage /></FeatureGate></S>}
                                requiredPermissions={['settings.api_keys']}
                            />
                        }
                    />

                    {/* Support — SITEMAP §198-201 (all users) */}
                    <Route path="support">
                        <Route index element={<PrivateRoute element={<S><SupportHomePage /></S>} />} />
                        <Route path="tickets" element={<PrivateRoute element={<S><SupportTicketsPage /></S>} />} />
                        <Route path="tickets/new" element={<PrivateRoute element={<S><SupportTicketNewPage /></S>} />} />
                        <Route path="changelog" element={<PrivateRoute element={<S><ChangelogPage /></S>} />} />
                    </Route>

                    {/* Shipping — EG-351 */}
                    <Route path="shipping">
                        <Route
                            index
                            element={<PrivateRoute element={<S><ShippingListPage /></S>} requiredPermissions={['shipping.view']} />}
                        />
                        <Route
                            path="new"
                            element={<PrivateRoute element={<S><ShippingNewPage /></S>} requiredPermissions={['shipping.manage']} />}
                        />
                        <Route
                            path="pickup-locations"
                            element={<PrivateRoute element={<S><PickupLocationsPage /></S>} requiredPermissions={['shipping.manage']} />}
                        />
                        <Route
                            path="ghtk-logs"
                            element={<PrivateRoute element={<S><GhtkLogsPage /></S>} requiredPermissions={['shipping.view']} />}
                        />
                        <Route
                            path=":id"
                            element={<PrivateRoute element={<S><ShippingDetailPage /></S>} requiredPermissions={['shipping.view']} />}
                        />
                    </Route>

                    {/* Accounting Dashboard — EG-605 */}
                    <Route
                        path="accounting/dashboard"
                        element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><DashboardOverviewPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                    />
                    <Route
                        path="accounting/data-repair"
                        element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><AccountingDataRepairPage /></FeatureGate></S>} requiredPermissions={['accounting.settings']} />}
                    />
                    <Route
                        path="settings/accounting"
                        element={<LegacyPathRedirect toBase={ROUTES.accounting.settings} />}
                    />
                    <Route
                        path="accounting/settings"
                        element={<PrivateRoute element={<S><AccountingSettingsPage /></S>} requiredPermissions={['accounting.settings']} />}
                    />
                    <Route path="accounting/reports">
                        <Route
                            index
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><AccountingReportsPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                        <Route
                            path="business-results"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><BusinessResultsReportPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                        <Route
                            path="balance-sheet"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><BalanceSheetReportPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                        <Route
                            path="trial-balance"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><TrialBalanceReportPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                        <Route
                            path="s2b-revenue"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><S2bRevenueLedgerPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                        <Route
                            path="s2c-income-expense"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><S2cIncomeExpenseDetailPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                        <Route
                            path="s2d-materials"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><S2dMaterialsDetailPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                        <Route
                            path="s2e-cash"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><S2eCashDetailPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                        <Route
                            path="inventory-summary"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><InventorySummaryReportPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                        <Route
                            path="cash-book"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><CashBookPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                        <Route
                            path="bank-deposit-book"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><BankDepositBookPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                        <Route
                            path="cash-receipt-journal"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><CashReceiptJournalPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                        <Route
                            path="cash-payment-journal"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><CashPaymentJournalPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                        <Route
                            path="cash-flow-statement"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><CashFlowStatementPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                        <Route
                            path="document-listing"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><DocumentListingPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                        <Route
                            path="voucher-listing"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><VoucherListingPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                        <Route
                            path="invoice-listing"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><InvoiceListingPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                        <Route
                            path="warehouse-voucher-listing"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><WarehouseVoucherListingPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                        <Route
                            path="receivable-payable-listing"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><ReceivablePayableListingPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                        <Route
                            path="customers-ar"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting-reports" minTier="professional"><CustomersARPage /></FeatureGate></S>} requiredPermissions={['accounting.reports']} />}
                        />
                    </Route>
                    <Route path="accounting/transactions" element={<Navigate to={ROUTES.accounting.transactions} replace />} />
                    <Route path="accounting/transactions/receipt/new" element={<Navigate to={ROUTES.accounting.receiptNew} replace />} />
                    <Route path="accounting/transactions/receipt/:id" element={<LegacyParamRedirect toBase="/accounting/cash-bank/receipts" />} />
                    <Route path="accounting/transactions/payment/new" element={<Navigate to={ROUTES.accounting.paymentNew} replace />} />
                    <Route path="accounting/transactions/payment/:id" element={<LegacyParamRedirect toBase="/accounting/cash-bank/payments" />} />
                    <Route path="accounting/transactions/internal-transfer/new" element={<Navigate to={ROUTES.accounting.internalTransferNew} replace />} />
                    <Route path="accounting/transactions/internal-transfer/:id" element={<LegacyParamRedirect toBase="/accounting/cash-bank/internal-transfers" />} />
                    <Route path="accounting/transactions/prepaid-expenses" element={<Navigate to={ROUTES.accounting.prepaidExpenses} replace />} />
                    <Route path="accounting/transactions/other" element={<Navigate to={ROUTES.accounting.otherDocument} replace />} />
                    <Route path="accounting/transactions/fixed-asset-groups" element={<Navigate to={ROUTES.accounting.fixedAssetGroups} replace />} />
                    <Route path="accounting/transactions/fixed-assets" element={<Navigate to={ROUTES.accounting.fixedAssets} replace />} />
                    <Route path="accounting/transactions/fixed-assets/:id" element={<LegacyParamRedirect toBase="/accounting/assets/fixed-assets" />} />
                    <Route path="accounting/transactions/tool-supplies" element={<Navigate to={ROUTES.accounting.toolSupplies} replace />} />
                    <Route path="accounting/transactions/tool-supplies/:id" element={<LegacyParamRedirect toBase="/accounting/assets/tool-supplies" />} />

                    {/* Accounting Transactions — S4.1 FE Accounting (EG-583 to EG-588) */}
                    <Route path="accounting/cash-bank">
                        <Route
                            index
                            element={<Navigate to={ROUTES.accounting.transactions} replace />}
                        />
                        <Route
                            path="transactions"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><TransactionsListPage /></FeatureGate></S>} requiredPermissions={['accounting.vouchers']} />}
                        />
                        <Route
                            path="receipts/new"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><ReceiptFormPage /></FeatureGate></S>} requiredPermissions={['accounting.vouchers']} />}
                        />
                        <Route
                            path="receipts/:id"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><ReceiptFormPage /></FeatureGate></S>} requiredPermissions={['accounting.vouchers']} />}
                        />
                        <Route
                            path="payments/new"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><PaymentFormPage /></FeatureGate></S>} requiredPermissions={['accounting.vouchers']} />}
                        />
                        <Route
                            path="payments/:id"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><PaymentFormPage /></FeatureGate></S>} requiredPermissions={['accounting.vouchers']} />}
                        />
                        <Route
                            path="internal-transfers/new"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><InternalTransferFormPage /></FeatureGate></S>} requiredPermissions={['accounting.vouchers']} />}
                        />
                        <Route
                            path="internal-transfers/:id"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><InternalTransferFormPage /></FeatureGate></S>} requiredPermissions={['accounting.vouchers']} />}
                        />
                    </Route>

                    <Route path="accounting/assets">
                        <Route
                            path="prepaid-expenses"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><PrepaidExpensesPage /></FeatureGate></S>} requiredPermissions={['accounting.vouchers']} />}
                        />
                        <Route
                            path="prepaid-expenses/new"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><PrepaidExpenseFormPage /></FeatureGate></S>} requiredPermissions={['accounting.vouchers']} />}
                        />
                        <Route
                            path="prepaid-expenses/:id"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><PrepaidExpenseFormPage /></FeatureGate></S>} requiredPermissions={['accounting.vouchers']} />}
                        />
                        <Route
                            path="fixed-asset-groups"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><FixedAssetGroupsPage /></FeatureGate></S>} requiredPermissions={['accounting.vouchers']} />}
                        />
                        <Route
                            path="fixed-assets"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><FixedAssetsPage /></FeatureGate></S>} requiredPermissions={['accounting.vouchers']} />}
                        />
                        <Route
                            path="fixed-assets/new"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><FixedAssetFormPage /></FeatureGate></S>} requiredPermissions={['accounting.vouchers']} />}
                        />
                        <Route
                            path="fixed-assets/:id"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><FixedAssetDetailPage /></FeatureGate></S>} requiredPermissions={['accounting.vouchers']} />}
                        />
                        <Route
                            path="tool-supplies/reports"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><ToolSupplyReportsPage /></FeatureGate></S>} requiredPermissions={['accounting.vouchers']} />}
                        />
                        <Route
                            path="tool-supplies"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><ToolSuppliesPage /></FeatureGate></S>} requiredPermissions={['accounting.vouchers']} />}
                        />
                        <Route
                            path="tool-supplies/new"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><ToolSupplyFormPage /></FeatureGate></S>} requiredPermissions={['accounting.vouchers']} />}
                        />
                        <Route
                            path="tool-supplies/:id"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><ToolSupplyDetailPage /></FeatureGate></S>} requiredPermissions={['accounting.vouchers']} />}
                        />
                    </Route>

                    <Route path="accounting/documents">
                        <Route
                            path="other"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><OtherDocumentPage /></FeatureGate></S>} requiredPermissions={['accounting.vouchers']} />}
                        />
                    </Route>

                    {/* Accounting Purchase Invoices — EG-575 to EG-577 (moved to /accounting/purchasing group above) */}

                    {/* Input Invoices — EG-578 */}
                    <Route
                        path="accounting/invoices/incoming"
                        element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><GovInvoicesPage /></FeatureGate></S>} requiredPermissions={['accounting.einvoice']} />}
                    />

                    {/* Accounting Inventory — EG-579 to EG-582 */}
                    <Route path="accounting/inventory">
                        <Route
                            path="receipts"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><AccountingReceiptsPage /></FeatureGate></S>} requiredPermissions={['accounting.inventory']} />}
                        />
                        <Route
                            path="issues"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><AccountingIssuesPage /></FeatureGate></S>} requiredPermissions={['accounting.inventory']} />}
                        />
                        <Route
                            path="transfers"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><AccountingTransfersPage /></FeatureGate></S>} requiredPermissions={['accounting.inventory']} />}
                        />
                        <Route
                            path="products-uom"
                            element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><ProductsUomPage /></FeatureGate></S>} requiredPermissions={['accounting.inventory']} />}
                        />
                    </Route>

                    {/* Customers AR — EG-573 */}
                    <Route
                        path="accounting/customers/ar"
                        element={<Navigate to={ROUTES.accounting.customersAR} replace />}
                    />
                    <Route
                        path="accounting/ar/customers"
                        element={<PrivateRoute element={<S><FeatureGate feature="accounting" minTier="professional"><CustomerARManagementPage /></FeatureGate></S>} requiredPermissions={['accounting.ar']} />}
                    />

                </Route>

                {/* 404 */}
                <Route path="*" element={<NotFoundPage />} />
            </Routes>
        </GoldPriceProvider>
    );
}

export default App;
